import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { notifyError } from "@/lib/notify";
import { cldThumb, cldVideoPoster, absUrl } from "@/lib/cld";
import { 
  LogOut, Users, Car, Plus, Trash2, Edit, RefreshCw, 
  Upload, X, Image, ChevronDown, Eye, Save, Loader2, Tag,
  AlertTriangle, ClipboardCheck, FileText, Wrench, Play,
  MapPin, History, ShieldCheck, Globe, BookOpen, Key, Clock, Check, CreditCard, Calendar,
  ShieldAlert, Landmark, Copy, ChevronUp, GripVertical
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { BODY_PARTS } from "@/components/PaintDiagram";
import { AdminBankSettingsTab } from "@/components/AdminBankSettingsTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("cars");
  const [inquiries, setInquiries] = useState([]);
  const [cars, setCars] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [dealerBids, setDealerBids] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [auctionFilter, setAuctionFilter] = useState("all");
  const [auctionDetail, setAuctionDetail] = useState(null);
  const [pendingCars, setPendingCars] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bookingSettings, setBookingSettings] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockedDate, setNewBlockedDate] = useState("");
  const [newLocation, setNewLocation] = useState({ name: "", address: "" });
  const [whatsappGroupUrl, setWhatsappGroupUrl] = useState("");
  const [waUrlSaving, setWaUrlSaving] = useState(false);
  const [contactSettings, setContactSettings] = useState({ phone: "", whatsapp: "", email: "" });
  const [contactSaving, setContactSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showCarModal, setShowCarModal] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [failedFiles, setFailedFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [carFormTab, setCarFormTab] = useState("basic");
  const [eurHufRate, setEurHufRate] = useState(400);
  const [rateEditing, setRateEditing] = useState(false);
  
  const emptyCarForm = {
    brand: "", model: "", year: "", mileage: "", fuel: "Benzin",
    engine: "", power: "", transmission: "Automata", drive: "",
    body_type: "", color: "", price: "", vin: "", equipment: "",
    description: "", images: [], featured: false, sold: false, reserve_price: 0,
    damages: [], inspection_report: {}, paint_thickness: {}, documents: [],
    motor_images: [], origin_country: "",
    vehicle_origin: {}, vehicle_inspection: {}, vehicle_history: {},
    tire_inspection: { primary: { label: "" }, secondary: { label: "" } },
    dealer_start_price: 0,
    dealer_auction_end_time: "",
    pickup_location_ids: [],
    premium_return_available: false,
    available_immediately: false,
    available_short_term: false,
  };

  const [carForm, setCarForm] = useState({ ...emptyCarForm });

  const navigate = useNavigate();
  const token = sessionStorage.getItem("adminToken");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [inquiriesRes, carsRes, rateRes, dealersRes, dealerBidsRes, pendingRes, ordersRes, bkSettingsRes, bkBlockedRes, waUrlRes, contactRes] = await Promise.all([
        axios.get(`${API}/admin/inquiries?token=${token}`),
        axios.get(`${API}/admin/cars?token=${token}`),
        axios.get(`${API}/settings/exchange-rate`),
        axios.get(`${API}/admin/dealers?token=${token}`),
        axios.get(`${API}/admin/dealer-bids?token=${token}`),
        axios.get(`${API}/admin/pending-cars?token=${token}`),
        axios.get(`${API}/admin/orders?token=${token}`),
        axios.get(`${API}/admin/booking/settings?token=${token}`),
        axios.get(`${API}/admin/booking/blocked-dates?token=${token}`),
        axios.get(`${API}/settings/whatsapp-group`),
        axios.get(`${API}/settings/contact`),
      ]);
      setInquiries(inquiriesRes.data);
      setCars(carsRes.data);
      setEurHufRate(rateRes.data.rate);
      setDealers(dealersRes.data);
      setDealerBids(dealerBidsRes.data);
      setPendingCars(pendingRes.data);
      setOrders(ordersRes.data);
      setBookingSettings(bkSettingsRes.data);
      setBlockedDates(bkBlockedRes.data);
      setWhatsappGroupUrl(waUrlRes.data?.url || "");
      setContactSettings(contactRes.data || { phone: "", whatsapp: "", email: "" });
      // Auctions loaded separately so failure doesn't break the whole dashboard.
      try {
        const auctionsRes = await axios.get(`${API}/admin/auctions?token=${token}`);
        setAuctions(auctionsRes.data || []);
      } catch { setAuctions([]); }
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem("adminToken");
        navigate("/admin");
      }
      toast.error("Hiba az adatok betöltésekor");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminToken");
    navigate("/admin");
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}?token=${token}`, { status });
      toast.success("Státusz frissítve");
      fetchData();
    } catch (error) {
      toast.error("Hiba történt");
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Biztosan törli?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}?token=${token}`);
      toast.success("Törölve");
      fetchData();
    } catch (error) {
      toast.error("Hiba történt");
    }
  };

  const resetCarForm = () => {
    setCarForm({ ...emptyCarForm });
  };

  const openCarModal = (car = null) => {
    if (car) {
      setEditingCar(car);
      setCarForm({
        brand: car.brand || "", model: car.model || "", year: car.year || "",
        mileage: car.mileage || "", fuel: car.fuel || "Benzin", engine: car.engine || "",
        power: car.power || "", transmission: car.transmission || "Automata",
        drive: car.drive || "", body_type: car.body_type || "", color: car.color || "",
        price: car.price || "", vin: car.vin || "", equipment: car.equipment || "",
        description: car.description || "", images: car.images || [],
        featured: car.featured || false, sold: car.sold || false,
        reserve_price: car.reserve_price || 0,
        damages: car.damages || [], inspection_report: car.inspection_report || {},
        paint_thickness: car.paint_thickness || {}, documents: car.documents || [],
        motor_images: car.motor_images || [], origin_country: car.origin_country || "",
        vehicle_origin: car.vehicle_origin || {}, vehicle_inspection: car.vehicle_inspection || {},
        vehicle_history: car.vehicle_history || {},
        tire_inspection: car.tire_inspection || { primary: { label: "" }, secondary: { label: "" } },
        dealer_start_price: car.dealer_start_price || 0,
        dealer_auction_end_time: car.dealer_auction_end_time || "",
        pickup_location_ids: car.pickup_location_ids || [],
        premium_return_available: car.premium_return_available || false,
        available_immediately: car.available_immediately || false,
        available_short_term: car.available_short_term || false,
      });
    } else {
      setEditingCar(null);
      resetCarForm();
    }
    setCarFormTab("basic");
    setFailedFiles([]);
    setShowCarModal(true);
  };

  // NEW ARCHITECTURE: one image = one HTTP request.
  // Uploads a single file and returns the Cloudinary URL. Throws with a rich
  // error payload (status, backend detail, filename) on failure so callers can
  // decide how to surface it and whether to allow retry.
  const uploadOneFile = useCallback(async (file, { timeoutMs = 120000, onProgress } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(
        `${API}/admin/upload-image?token=${token}`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: timeoutMs,
          onUploadProgress: (evt) => {
            if (onProgress && evt.total) onProgress(evt.loaded / evt.total);
          },
        }
      );
      if (!data?.url) throw new Error("Backend URL nélkül válaszolt");
      return { url: data.url, filename: data.filename || file.name };
    } catch (err) {
      const status = err?.response?.status;
      const backend = err?.response?.data;
      const reason = backend?.detail || err?.message || "Hálózati hiba";
      const enriched = new Error(reason);
      enriched.status = status;
      enriched.backend = backend;
      enriched.reason = reason;
      enriched.filename = file.name;
      throw enriched;
    }
  }, [token]);

  // Sequential multi-file upload → { urls, failed }.
  // Loops one file at a time using the NEW singular endpoint. Same signature as
  // the old batch helper so damages/docs/motor upload flows keep working unchanged.
  const uploadMany = useCallback(async (files, { timeoutMs = 120000 } = {}) => {
    if (!files || files.length === 0) return { urls: [], failed: [] };
    const urls = [];
    const failed = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const { url } = await uploadOneFile(f, { timeoutMs });
        urls.push(url);
      } catch (err) {
        failed.push({ filename: f.name, reason: err?.reason || err?.message || "Hiba" });
        notifyError(`Sikertelen: ${f.name}`, {
          filename: f.name,
          status: err?.status,
          reason: err?.reason,
          backend: err?.backend,
          error: err,
        });
      }
    }
    return { urls, failed };
  }, [uploadOneFile]);

  // Main car-images upload path used by drag/drop + file input in the Edit Car modal.
  // - Uploads images ONE-BY-ONE (each is a fully independent HTTP request).
  // - Adds every successful URL to the form IMMEDIATELY (streaming success), so an
  //   error late in the queue can NEVER wipe out already-completed uploads.
  // - Failed files are pushed to `failedFiles`, from where the user can retry each
  //   individually — successful uploads are never restarted.
  const uploadFilesChunked = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    let succeeded = 0;
    const failuresThisBatch = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setUploadProgress({ done: i, total: files.length });
      try {
        const { url } = await uploadOneFile(f);
        // Stream success into the form so nothing is lost even if a later file fails.
        setCarForm(prev => ({ ...prev, images: [...prev.images, url] }));
        succeeded++;
      } catch (err) {
        const entry = {
          id: `${Date.now()}-${i}-${f.name}`,
          file: f,
          reason: err?.reason || err?.message || "Hiba",
          status: err?.status,
          backend: err?.backend,
        };
        failuresThisBatch.push(entry);
        setFailedFiles(prev => [...prev, entry]);
        notifyError(`Sikertelen: ${f.name}`, {
          filename: f.name,
          status: err?.status,
          reason: err?.reason,
          backend: err?.backend,
          error: err,
        });
      }
    }
    setUploadProgress({ done: files.length, total: files.length });
    if (succeeded > 0) {
      toast.success(
        `${succeeded} kép feltöltve${failuresThisBatch.length > 0 ? `, ${failuresThisBatch.length} sikertelen` : ""}`,
      );
    } else if (failuresThisBatch.length > 0) {
      notifyError("Egyetlen képet sem sikerült feltölteni", {
        reason: `${failuresThisBatch.length} fájl hibázott — lásd a Sikertelen feltöltések panelt.`,
      });
    }
    setUploading(false);
    setUploadProgress({ done: 0, total: 0 });
  }, [uploadOneFile]);

  // Retry a single failed upload — never touches the previously successful ones.
  const retryFailedFile = useCallback(async (entry) => {
    try {
      const { url } = await uploadOneFile(entry.file);
      setCarForm(prev => ({ ...prev, images: [...prev.images, url] }));
      setFailedFiles(prev => prev.filter(e => e.id !== entry.id));
      toast.success(`Sikeres újrapróbálkozás: ${entry.file.name}`);
    } catch (err) {
      // Keep the entry, refresh its reason, and show the fresh error.
      setFailedFiles(prev => prev.map(e => e.id === entry.id
        ? { ...e, reason: err?.reason || err?.message || "Hiba", status: err?.status, backend: err?.backend }
        : e));
      notifyError(`Újrapróba sikertelen: ${entry.file.name}`, {
        filename: entry.file.name,
        status: err?.status,
        reason: err?.reason,
        backend: err?.backend,
        error: err,
      });
    }
  }, [uploadOneFile]);

  const dismissFailedFile = useCallback((entryId) => {
    setFailedFiles(prev => prev.filter(e => e.id !== entryId));
  }, []);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    // Reset input so re-selecting same files re-triggers onChange
    e.target.value = "";
    await uploadFilesChunked(files);
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
    await uploadFilesChunked(files);
  }, [uploadFilesChunked]);

  const removeImage = (index) => {
    setCarForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const dragImg = useRef(null);
  const moveImage = (from, to) => {
    setCarForm(prev => {
      const imgs = [...(prev.images || [])];
      if (from == null || to == null || from < 0 || to < 0 || from >= imgs.length || to >= imgs.length || from === to) return prev;
      const [m] = imgs.splice(from, 1);
      imgs.splice(to, 0, m);
      return { ...prev, images: imgs };
    });
  };

  const handleCarSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingCar) {
        await axios.put(`${API}/admin/cars/${editingCar.id}?token=${token}`, carForm);
        toast.success("Autó frissítve");
      } else {
        await axios.post(`${API}/admin/cars?token=${token}`, carForm);
        toast.success("Autó hozzáadva");
      }
      setShowCarModal(false);
      fetchData();
    } catch (error) {
      toast.error("Hiba történt");
    } finally {
      setSaving(false);
    }
  };

  const deleteCar = async (id) => {
    if (!window.confirm("Biztosan törli ezt az autót?")) return;
    try {
      await axios.delete(`${API}/admin/cars/${id}?token=${token}`);
      toast.success("Autó törölve");
      fetchData();
    } catch (error) {
      toast.error("Hiba történt");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      contacted: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      completed: "bg-green-500/20 text-green-400 border-green-500/30"
    };
    const labels = { new: "Új", contacted: "Folyamatban", completed: "Lezárt" };
    return <span className={`px-2 py-1 text-xs rounded-full border ${styles[status]}`}>{labels[status]}</span>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("hu-HU", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  // Dealer management
  const handleDealerApprove = async (dealerId) => {
    try {
      await axios.put(`${API}/admin/dealers/${dealerId}/approve?token=${token}`);
      toast.success("Kereskedő jóváhagyva");
      fetchData();
    } catch { toast.error("Hiba történt"); }
  };

  const handleDealerReject = async (dealerId) => {
    if (!window.confirm("Biztosan elutasítja ezt a kereskedőt?")) return;
    try {
      await axios.put(`${API}/admin/dealers/${dealerId}/reject?token=${token}`);
      toast.success("Kereskedő elutasítva");
      fetchData();
    } catch { toast.error("Hiba történt"); }
  };

  // Phase 3: auction admin actions
  const refreshAuctions = async () => {
    try {
      const r = await axios.get(`${API}/admin/auctions?token=${token}`);
      setAuctions(r.data || []);
    } catch { /* keep old list */ }
  };

  const auctionAction = async (carId, action, body = {}) => {
    try {
      const r = await axios.post(`${API}/admin/auctions/${carId}/${action}?token=${token}`, body);
      toast.success(`OK: ${action} → ${r.data?.auction_status || r.data?.status || ""}`);
      await refreshAuctions();
      if (auctionDetail?.car?.id === carId) openAuctionDetail(carId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Hiba: ${action}`);
    }
  };

  // Phase 5 UI: payment actions
  const [paymentEvents, setPaymentEvents] = useState([]);
  const loadPaymentEvents = async (orderId) => {
    try {
      const r = await axios.get(`${API}/admin/orders/${orderId}/payment-events?token=${token}`);
      setPaymentEvents(r.data || []);
    } catch { setPaymentEvents([]); }
  };

  const confirmPayment = async (orderId) => {
    if (!window.confirm("Megerősíti a fizetést? Ez már nem visszavonható automatikusan.")) return;
    try {
      await axios.put(`${API}/admin/orders/${orderId}/payment-status?token=${token}`,
                      { payment_status: "confirmed" });
      toast.success("Fizetés megerősítve");
      if (auctionDetail?.car?.id) await openAuctionDetail(auctionDetail.car.id);
      loadPaymentEvents(orderId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Hiba"); }
  };

  const rejectPayment = async (orderId) => {
    if (!window.confirm("Elutasítja a fizetést?")) return;
    try {
      await axios.put(`${API}/admin/orders/${orderId}/payment-status?token=${token}`,
                      { payment_status: "failed" });
      toast.success("Fizetés elutasítva");
      if (auctionDetail?.car?.id) openAuctionDetail(auctionDetail.car.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Hiba"); }
  };

  const extendPaymentDeadline = async (orderId) => {
    const h = parseInt(window.prompt("Meghosszabbítás órákban:", "24") || "0", 10);
    if (!h || h <= 0) return;
    const reason = window.prompt("Indok:", "") || "";
    try {
      await axios.put(`${API}/admin/orders/${orderId}/extend-payment-deadline?token=${token}&hours=${h}&reason=${encodeURIComponent(reason)}`);
      toast.success("Határidő meghosszabbítva");
      if (auctionDetail?.car?.id) openAuctionDetail(auctionDetail.car.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Hiba"); }
  };

  const openAuctionDetail = async (carId) => {
    setPaymentEvents([]);
    try {
      const r = await axios.get(`${API}/admin/auctions/${carId}?token=${token}`);
      setAuctionDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hiba");
    }
  };

  const startAuction = (carId) => {
    const h = parseFloat(window.prompt("Aukció hossza órákban:", "24") || "0");
    if (!h || h <= 0) return;
    auctionAction(carId, "start", { duration_hours: h });
  };

  const extendAuction = (carId) => {
    const m = parseInt(window.prompt("Meghosszabbítás percekben:", "10") || "0", 10);
    if (!m || m <= 0) return;
    auctionAction(carId, "extend", { minutes: m });
  };

  const endAuction = (carId) => {
    if (!window.confirm("Biztosan lezárja most az aukciót?")) return;
    auctionAction(carId, "end");
  };

  const auctionStatusBadge = (status) => {
    const map = {
      draft: "bg-white/5 text-white/60 border-white/10",
      scheduled: "bg-blue-500/10 text-blue-400 border-blue-500/30",
      live: "bg-green-500/10 text-green-400 border-green-500/30",
      paused: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
      ended: "bg-white/10 text-white/70 border-white/20",
      sold: "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30",
      unsold: "bg-red-500/10 text-red-400 border-red-500/30",
    };
    return <span className={`px-2 py-0.5 text-[10px] rounded-full border ${map[status] || map.draft}`} data-testid={`auction-status-${status}`}>{(status || "draft").toUpperCase()}</span>;
  };

  const filteredAuctions = auctions.filter(a => {
    if (auctionFilter === "all") return true;
    return (a.auction_status || "draft") === auctionFilter;
  });

  // Phase 1: KYC / verification actions
  const handleDealerSuspend = async (dealerId) => {
    if (!window.confirm("Felfüggeszti ezt a kereskedőt?")) return;
    try {
      await axios.put(`${API}/admin/dealers/${dealerId}/suspend?token=${token}`);
      toast.success("Felfüggesztve");
      fetchData();
    } catch { toast.error("Hiba történt"); }
  };

  const handleDealerReactivate = async (dealerId) => {
    try {
      await axios.put(`${API}/admin/dealers/${dealerId}/reactivate?token=${token}`);
      toast.success("Újraaktiválva");
      fetchData();
    } catch { toast.error("Hiba történt"); }
  };

  const handleDealerSetLimit = async (dealerId) => {
    const raw = window.prompt("Új licit-limit (EUR, 0 = korlátlan):", "0");
    if (raw === null) return;
    const limit = parseFloat(raw);
    if (Number.isNaN(limit) || limit < 0) return toast.error("Érvénytelen érték");
    try {
      await axios.put(`${API}/admin/dealers/${dealerId}/verify?token=${token}`, {
        kyc_status: "verified",
        bid_limit_eur: limit,
      });
      toast.success(`Licit-limit: ${limit} €`);
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hiba történt"); }
  };

  const handleDealerNote = async (dealerId, currentNote = "") => {
    const note = window.prompt("Belső megjegyzés:", currentNote || "");
    if (note === null) return;
    try {
      await axios.put(`${API}/admin/dealers/${dealerId}/verify?token=${token}`, {
        kyc_status: "verified",
        verification_notes: note,
      });
      toast.success("Megjegyzés mentve");
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hiba történt"); }
  };

  const handleDealerVatCheck = async (dealerId) => {
    try {
      const r = await axios.post(`${API}/admin/dealers/${dealerId}/vat-check?token=${token}`);
      toast.info(r.data?.message || `VAT: ${r.data?.vat_check_status}`);
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hiba történt"); }
  };

  const getKycBadge = (status) => {
    const styles = {
      verified: "bg-green-500/20 text-green-400 border-green-500/30",
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      under_review: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      rejected: "bg-red-500/20 text-red-400 border-red-500/30",
      suspended: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    };
    const label = { verified: "Verified", pending: "Pending", under_review: "Review", rejected: "Rejected", suspended: "Suspended" }[status] || status;
    return <span className={`px-2 py-0.5 text-[10px] rounded-full border ${styles[status] || "bg-white/5 text-white/40 border-white/10"}`} data-testid={`kyc-badge-${status}`}>{label}</span>;
  };

  const getDealerRoleBadge = (role) => {
    const styles = {
      pending_dealer: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      dealer_user: "bg-green-500/20 text-green-400 border-green-500/30",
      rejected: "bg-red-500/20 text-red-400 border-red-500/30",
    };
    const labels = { pending_dealer: "Jóváhagyásra vár", dealer_user: "Jóváhagyva", rejected: "Elutasítva" };
    return <span className={`px-2 py-1 text-xs rounded-full border ${styles[role] || ""}`}>{labels[role] || role}</span>;
  };

  const handleApproveCar = async (carId) => {
    try {
      await axios.put(`${API}/admin/cars/${carId}/approve-submission?token=${token}`);
      toast.success("Hirdetés jóváhagyva és megjelent az oldalon!");
      fetchData();
    } catch { toast.error("Hiba történt"); }
  };

  const handleRejectCar = async (carId) => {
    if (!window.confirm("Biztosan elutasítja ezt a hirdetést?")) return;
    try {
      await axios.put(`${API}/admin/cars/${carId}/reject-submission?token=${token}`);
      toast.success("Hirdetés elutasítva");
      fetchData();
    } catch { toast.error("Hiba történt"); }
  };

  // Booking settings handlers
  const saveBookingSettings = async () => {
    if (!bookingSettings) return;
    try {
      await axios.put(`${API}/admin/booking/settings?token=${token}`, {
        available_weekdays: bookingSettings.available_weekdays,
        time_slots: bookingSettings.time_slots,
        max_bookings_per_slot: Number(bookingSettings.max_bookings_per_slot),
        advance_days: Number(bookingSettings.advance_days),
        horizon_days: Number(bookingSettings.horizon_days),
        pickup_locations: bookingSettings.pickup_locations,
      });
      toast.success("Beállítások mentve");
      fetchData();
    } catch { toast.error("Hiba a mentéskor"); }
  };

  const toggleWeekday = (wd) => {
    const cur = new Set(bookingSettings.available_weekdays || []);
    if (cur.has(wd)) cur.delete(wd); else cur.add(wd);
    setBookingSettings({ ...bookingSettings, available_weekdays: Array.from(cur).sort() });
  };

  const addTimeSlot = (time) => {
    if (!time) return;
    const slots = Array.from(new Set([...(bookingSettings.time_slots || []), time])).sort();
    setBookingSettings({ ...bookingSettings, time_slots: slots });
  };

  const removeTimeSlot = (time) => {
    setBookingSettings({ ...bookingSettings, time_slots: bookingSettings.time_slots.filter(t => t !== time) });
  };

  const addPickupLocation = () => {
    if (!newLocation.name.trim()) return;
    const loc = { id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()), name: newLocation.name.trim(), address: newLocation.address.trim() };
    setBookingSettings({ ...bookingSettings, pickup_locations: [...(bookingSettings.pickup_locations || []), loc] });
    setNewLocation({ name: "", address: "" });
  };

  const removePickupLocation = (id) => {
    setBookingSettings({ ...bookingSettings, pickup_locations: bookingSettings.pickup_locations.filter(l => l.id !== id) });
  };

  const addBlockedDate = async () => {
    if (!newBlockedDate) return;
    try {
      await axios.post(`${API}/admin/booking/blocked-dates?token=${token}`, { date: newBlockedDate });
      toast.success("Dátum zárolva");
      setNewBlockedDate("");
      fetchData();
    } catch { toast.error("Hiba a zároláskor"); }
  };

  const removeBlockedDate = async (date) => {
    try {
      await axios.delete(`${API}/admin/booking/blocked-dates/${date}?token=${token}`);
      toast.success("Zárolás feloldva");
      fetchData();
    } catch { toast.error("Hiba a feloldáskor"); }
  };

  // Memoized filtered lists to avoid re-computing on every render
  const pendingDealers = useMemo(() => dealers.filter(d => d.role === "pending_dealer"), [dealers]);
  const approvedDealers = useMemo(() => dealers.filter(d => d.role === "dealer_user"), [dealers]);

  return (
    <div className="min-h-screen bg-[#030303]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#080808] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src="/alpine-car-logo.png" alt="Alpine Car" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-cover" />
            <div>
              <span className="font-bold text-white text-sm sm:text-lg">Alpine Car</span>
              <p className="text-[10px] sm:text-xs text-white/40">Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Exchange rate */}
            <div className="flex items-center gap-1.5 px-2 py-1 bg-white/5 rounded-lg">
              <span className="text-[10px] text-white/40 hidden sm:inline">EUR→HUF</span>
              <input type="number" value={eurHufRate}
                onChange={(e) => { setEurHufRate(Number(e.target.value)); setRateEditing(true); }}
                className="w-14 bg-transparent border-none text-xs text-[#D4AF37] font-semibold text-center focus:outline-none"
                data-testid="exchange-rate-input" />
              {rateEditing && (
                <button onClick={async () => {
                  try {
                    await axios.put(`${API}/admin/exchange-rate?token=${token}`, { rate: eurHufRate });
                    toast.success("Árfolyam mentve");
                    setRateEditing(false);
                  } catch { toast.error("Hiba"); }
                }} className="text-[10px] bg-[#D4AF37] text-black px-1.5 py-0.5 rounded font-semibold" data-testid="exchange-rate-save">
                  <Save className="w-3 h-3" />
                </button>
              )}
            </div>
            <Button onClick={fetchData} variant="ghost" size="sm" className="text-white/50 hover:text-white p-2">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-white/50 hover:text-white p-2">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-white/5 bg-[#080808] sticky top-[57px] sm:top-[65px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-4 sm:gap-8">
            <button
              onClick={() => setActiveTab("cars")}
              className={`py-3 sm:py-4 px-1 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 ${
                activeTab === "cars" ? "border-[#D4AF37] text-white" : "border-transparent text-white/50"
              }`}
            >
              <Car className="w-4 h-4" /> Autók ({cars.length})
            </button>
            <button
              onClick={() => setActiveTab("inquiries")}
              className={`py-3 sm:py-4 px-1 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 ${
                activeTab === "inquiries" ? "border-[#D4AF37] text-white" : "border-transparent text-white/50"
              }`}
            >
              <Users className="w-4 h-4" /> Érdeklődések ({inquiries.length})
            </button>
            <button
              onClick={() => setActiveTab("dealers")}
              className={`py-3 sm:py-4 px-1 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 ${
                activeTab === "dealers" ? "border-[#D4AF37] text-white" : "border-transparent text-white/50"
              }`}
              data-testid="dealers-tab"
            >
              <ShieldCheck className="w-4 h-4" /> Kereskedők ({dealers.length})
            </button>
            <button
              onClick={() => setActiveTab("dealer-bids")}
              className={`py-3 sm:py-4 px-1 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 ${
                activeTab === "dealer-bids" ? "border-[#D4AF37] text-white" : "border-transparent text-white/50"
              }`}
              data-testid="dealer-bids-tab"
            >
              <Clock className="w-4 h-4" /> Licitek ({dealerBids.length})
            </button>
            <button
              onClick={() => setActiveTab("auctions")}
              className={`py-3 sm:py-4 px-1 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 ${
                activeTab === "auctions" ? "border-[#D4AF37] text-white" : "border-transparent text-white/50"
              }`}
              data-testid="auctions-tab"
            >
              <ShieldCheck className="w-4 h-4" /> Aukciók ({auctions.length})
            </button>
            <button
              onClick={() => setActiveTab("pending")}
              className={`py-3 sm:py-4 px-1 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 ${
                activeTab === "pending" ? "border-[#D4AF37] text-white" : "border-transparent text-white/50"
              }`}
              data-testid="pending-cars-tab"
            >
              <AlertTriangle className="w-4 h-4" /> Hirdetések ({pendingCars.length})
            </button>
            <button
              onClick={() => setActiveTab("orders")}
              className={`py-3 sm:py-4 px-1 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 ${
                activeTab === "orders" ? "border-[#D4AF37] text-white" : "border-transparent text-white/50"
              }`}
              data-testid="orders-tab"
            >
              <CreditCard className="w-4 h-4" /> Rendelések ({orders.length})
            </button>
            <button
              onClick={() => setActiveTab("booking")}
              className={`py-3 sm:py-4 px-1 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 ${
                activeTab === "booking" ? "border-[#D4AF37] text-white" : "border-transparent text-white/50"
              }`}
              data-testid="booking-tab"
            >
              <Calendar className="w-4 h-4" /> Időpontok
            </button>
            <button
              onClick={() => setActiveTab("bank")}
              className={`py-3 sm:py-4 px-1 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 ${
                activeTab === "bank" ? "border-[#D4AF37] text-white" : "border-transparent text-white/50"
              }`}
              data-testid="bank-tab"
            >
              <Landmark className="w-4 h-4" /> Banki adatok
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : (
          <>
            {/* Cars Tab */}
            {activeTab === "cars" && (
              <div>
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-white">Autók kezelése</h2>
                  <Button onClick={() => openCarModal()} data-testid="admin-add-car-btn" className="bg-[#D4AF37] text-black hover:bg-[#B8860B] text-xs sm:text-sm">
                    <Plus className="w-4 h-4 mr-1 sm:mr-2" /> Új autó
                  </Button>
                </div>

                {cars.length > 0 ? (
                  <div className="grid gap-4">
                    {cars.map((car) => (
                      <div key={car.id} className="bg-[#0A0A0A] border border-white/5 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <div className="w-full sm:w-32 h-24 sm:h-20 bg-[#1a1a1a] rounded-lg overflow-hidden flex-shrink-0">
                          {car.images?.[0] ? (
                            <img src={car.images[0].startsWith("/api") ? `${BACKEND_URL}${car.images[0]}` : car.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white/20">
                              <Image className="w-8 h-8" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold text-white text-sm sm:text-base">{car.brand} {car.model}</h3>
                              <p className="text-xs sm:text-sm text-white/50">{car.year} • {car.mileage} • {car.fuel}</p>
                            </div>
                            <span className="text-[#D4AF37] font-bold text-sm sm:text-base whitespace-nowrap">{car.price}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-2 sm:mt-3">
                            {car.sold && <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded">Eladva</span>}
                            {car.featured && <span className="text-xs px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] rounded">Kiemelt</span>}
                          </div>
                        </div>
                        <div className="flex sm:flex-col gap-2 justify-end">
                          <Button onClick={() => window.open(`/car/${car.id}`, '_blank')} variant="ghost" size="sm" className="text-white/50 hover:text-white" data-testid={`admin-view-car-${car.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button onClick={() => openCarModal(car)} variant="ghost" size="sm" className="text-white/50 hover:text-white" data-testid={`admin-edit-car-${car.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button onClick={() => deleteCar(car.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300" data-testid={`admin-delete-car-${car.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-[#0A0A0A] rounded-xl border border-white/5">
                    <Car className="w-12 h-12 mx-auto text-white/20 mb-4" />
                    <p className="text-white/50 mb-4">Még nincsenek autók</p>
                    <Button onClick={() => openCarModal()} className="bg-[#D4AF37] text-black hover:bg-[#B8860B]">
                      <Plus className="w-4 h-4 mr-2" /> Első autó hozzáadása
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Inquiries Tab */}
            {activeTab === "inquiries" && (
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-6">Érdeklődések</h2>
                {inquiries.length > 0 ? (
                  <div className="grid gap-3 sm:gap-4">
                    {inquiries.map((inq) => (
                      <div key={inq.id} className="bg-[#0A0A0A] border border-white/5 rounded-xl p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white text-sm sm:text-base">{inq.name}</span>
                            {getStatusBadge(inq.status)}
                          </div>
                          <span className="text-xs text-white/40">{formatDate(inq.created_at)}</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs sm:text-sm text-white/60 mb-3">
                          <div><span className="text-white/40">Tel:</span> {inq.phone}</div>
                          <div><span className="text-white/40">Márka:</span> {inq.brand}</div>
                          <div><span className="text-white/40">Keret:</span> {inq.budget}</div>
                          <div><span className="text-white/40">Év:</span> {inq.year || "-"}</div>
                        </div>
                        {inq.message && <p className="text-xs text-white/50 mb-3 bg-white/5 p-2 rounded">{inq.message}</p>}
                        <div className="flex items-center justify-between">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-white/60 hover:text-white text-xs">
                                Státusz <ChevronDown className="w-3 h-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[#1A1A1A] border-white/10">
                              <DropdownMenuItem onClick={() => updateInquiryStatus(inq.id, "new")} className="text-white text-xs">Új</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => updateInquiryStatus(inq.id, "contacted")} className="text-white text-xs">Folyamatban</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => updateInquiryStatus(inq.id, "completed")} className="text-white text-xs">Lezárt</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button onClick={() => deleteInquiry(inq.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-[#0A0A0A] rounded-xl border border-white/5">
                    <Users className="w-12 h-12 mx-auto text-white/20 mb-4" />
                    <p className="text-white/50">Még nincsenek érdeklődések</p>
                  </div>
                )}
              </div>
            )}


            {/* Dealers Tab */}
            {activeTab === "dealers" && (
              <div className="space-y-4" data-testid="dealers-panel">
                {/* Pending Dealers */}
                {pendingDealers.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-yellow-400 mb-3">Jóváhagyásra váró kereskedők</h3>
                    <div className="space-y-3">
                      {pendingDealers.map((d) => (
                        <div key={d.id} className="bg-[#0A0A0A] border border-yellow-500/20 rounded-xl p-4" data-testid={`dealer-${d.id}`}>
                          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-white">{d.full_name}</span>
                                {getDealerRoleBadge(d.role)}
                              </div>
                              <p className="text-sm text-white/60">{d.company_name}</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs text-white/40">
                                <span>Email: <span className="text-white/60">{d.email}</span></span>
                                <span>Telefon: <span className="text-white/60">{d.phone}</span></span>
                                <span>ÁFA szám: <span className="text-white/60">{d.eu_vat_number}</span></span>
                                <span>Ország: <span className="text-white/60">{d.country}</span></span>
                                <span className="col-span-2">Cím: <span className="text-white/60">{d.company_address}</span></span>
                              </div>
                              {d.document_url && (
                                <a href={`${BACKEND_URL}${d.document_url}`} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline mt-2">
                                  <FileText className="w-3.5 h-3.5" /> Dokumentum megtekintése
                                </a>
                              )}
                            </div>
                            <div className="flex gap-2 sm:flex-col">
                              <Button size="sm" onClick={() => handleDealerApprove(d.id)}
                                className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`approve-${d.id}`}>
                                Jóváhagyás
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleDealerReject(d.id)}
                                className="text-xs" data-testid={`reject-${d.id}`}>
                                Elutasítás
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Dealers */}
                <div>
                  <h3 className="text-sm font-medium text-white/60 mb-3">Összes kereskedő</h3>
                  {dealers.length > 0 ? (
                    <div className="bg-[#0A0A0A] border border-white/5 rounded-xl overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-white/5">
                              <th className="text-left p-3 text-white/40 font-medium">Név</th>
                              <th className="text-left p-3 text-white/40 font-medium">Cég</th>
                              <th className="text-left p-3 text-white/40 font-medium">ÁFA szám</th>
                              <th className="text-left p-3 text-white/40 font-medium">Email</th>
                              <th className="text-left p-3 text-white/40 font-medium">Státusz</th>
                              <th className="text-left p-3 text-white/40 font-medium">KYC</th>
                              <th className="text-left p-3 text-white/40 font-medium">Limit</th>
                              <th className="text-left p-3 text-white/40 font-medium">VAT</th>
                              <th className="text-left p-3 text-white/40 font-medium">Műveletek</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dealers.map((d) => (
                              <tr key={d.id} className="border-b border-white/5 last:border-0" data-testid={`dealer-row-${d.id}`}>
                                <td className="p-3 text-white">
                                  {d.full_name}
                                  {d.representative_full_name && d.representative_full_name !== d.full_name && (
                                    <div className="text-[10px] text-white/40">Képv.: {d.representative_full_name}</div>
                                  )}
                                </td>
                                <td className="p-3 text-white/70">
                                  {d.company_name}
                                  {d.company_registration_number && <div className="text-[10px] text-white/40">#{d.company_registration_number}</div>}
                                </td>
                                <td className="p-3 text-white/70 font-mono">{d.eu_vat_number}</td>
                                <td className="p-3 text-white/70">{d.email}</td>
                                <td className="p-3">{getDealerRoleBadge(d.role)}</td>
                                <td className="p-3">{getKycBadge(d.kyc_status || (d.role === "dealer_user" ? "verified" : "pending"))}</td>
                                <td className="p-3 text-white/70">{d.bid_limit_eur ? `${d.bid_limit_eur.toLocaleString("hu-HU")} €` : "-"}</td>
                                <td className="p-3 text-white/50 text-[10px]">{d.vat_check_status || "not_checked"}</td>
                                <td className="p-3">
                                  <div className="flex flex-wrap gap-1">
                                    {d.role !== "dealer_user" || d.kyc_status !== "verified" ? (
                                      <button onClick={() => handleDealerApprove(d.id)} className="px-2 py-1 rounded bg-green-600/20 text-green-400 hover:bg-green-600/30 text-[10px]" data-testid={`verify-${d.id}`}>Verify</button>
                                    ) : null}
                                    {d.kyc_status === "verified" && (
                                      <button onClick={() => handleDealerSuspend(d.id)} className="px-2 py-1 rounded bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 text-[10px]" data-testid={`suspend-${d.id}`}>Suspend</button>
                                    )}
                                    {d.kyc_status === "suspended" && (
                                      <button onClick={() => handleDealerReactivate(d.id)} className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 text-[10px]" data-testid={`reactivate-${d.id}`}>Reactivate</button>
                                    )}
                                    <button onClick={() => handleDealerSetLimit(d.id)} className="px-2 py-1 rounded bg-white/5 text-white/70 hover:bg-white/10 text-[10px]" data-testid={`limit-${d.id}`}>Limit</button>
                                    <button onClick={() => handleDealerNote(d.id, d.verification_notes)} className="px-2 py-1 rounded bg-white/5 text-white/70 hover:bg-white/10 text-[10px]" data-testid={`note-${d.id}`}>Note</button>
                                    <button onClick={() => handleDealerVatCheck(d.id)} className="px-2 py-1 rounded bg-white/5 text-white/70 hover:bg-white/10 text-[10px]" data-testid={`vat-${d.id}`}>VAT</button>
                                    {d.document_url && (
                                      <a href={d.document_url} target="_blank" rel="noopener noreferrer" className="px-2 py-1 rounded bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 text-[10px]" data-testid={`doc-${d.id}`}>Doc</a>
                                    )}
                                    {d.document_url_extra && (
                                      <a href={d.document_url_extra} target="_blank" rel="noopener noreferrer" className="px-2 py-1 rounded bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 text-[10px]" data-testid={`doc-extra-${d.id}`}>Doc2</a>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-16 bg-[#0A0A0A] rounded-xl border border-white/5">
                      <ShieldCheck className="w-12 h-12 mx-auto text-white/20 mb-4" />
                      <p className="text-white/50">Még nincsenek kereskedők</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Dealer Bids Tab */}
            {activeTab === "dealer-bids" && (
              <div className="space-y-4" data-testid="dealer-bids-panel">
                {dealerBids.length > 0 ? (
                  <div className="bg-[#0A0A0A] border border-white/5 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-white/5">
                            <th className="text-left p-3 text-white/40 font-medium">Kereskedő</th>
                            <th className="text-left p-3 text-white/40 font-medium">Cég</th>
                            <th className="text-left p-3 text-white/40 font-medium">Autó</th>
                            <th className="text-right p-3 text-white/40 font-medium">Licit összeg</th>
                            <th className="text-left p-3 text-white/40 font-medium">Dátum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dealerBids.map((bid) => {
                            const car = cars.find(c => c.id === bid.car_id);
                            const carName = car ? `${car.brand} ${car.model}` : bid.car_id.substring(0, 8);
                            const isHighest = car && bid.amount >= (car.dealer_highest_bid || 0);
                            return (
                              <tr key={bid.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                                <td className="p-3 text-white font-medium">{bid.dealer_name}</td>
                                <td className="p-3 text-white/60">{bid.company_name}</td>
                                <td className="p-3 text-white/70">{carName}</td>
                                <td className="p-3 text-right">
                                  <span className={`font-bold ${isHighest ? "text-green-400" : "text-white/70"}`}>
                                    {bid.amount?.toLocaleString("hu-HU")} €
                                  </span>
                                  {isHighest && <span className="ml-1 text-[10px] text-green-400/60">vezető</span>}
                                </td>
                                <td className="p-3 text-white/40">{formatDate(bid.created_at)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-16 bg-[#0A0A0A] rounded-xl border border-white/5">
                    <Clock className="w-12 h-12 mx-auto text-white/20 mb-4" />
                    <p className="text-white/50">Még nincsenek kereskedői licitek</p>
                  </div>
                )}
              </div>
            )}

            {/* Auctions Tab (Phase 3) */}
            {activeTab === "auctions" && (
              <div className="space-y-4" data-testid="auctions-panel">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h2 className="text-lg sm:text-xl font-semibold text-white">Aukciók</h2>
                  <div className="flex gap-1 flex-wrap">
                    {["all","draft","scheduled","live","paused","ended","sold","unsold"].map(s => (
                      <button
                        key={s}
                        onClick={() => setAuctionFilter(s)}
                        className={`px-2 py-1 text-[10px] rounded-full border transition-colors ${auctionFilter === s ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-white/50 hover:border-white/20"}`}
                        data-testid={`auction-filter-${s}`}
                      >{s === "all" ? "Összes" : s.charAt(0).toUpperCase()+s.slice(1)}</button>
                    ))}
                  </div>
                </div>
                {filteredAuctions.length === 0 ? (
                  <div className="text-center py-12 text-white/40 text-sm">Nincs a szűrésnek megfelelő aukció</div>
                ) : (
                  <div className="grid gap-3">
                    {filteredAuctions.map(a => {
                      const status = a.auction_status || "draft";
                      const endStr = a.dealer_auction_end_time ? new Date(a.dealer_auction_end_time).toLocaleString("hu-HU") : "-";
                      return (
                        <div key={a.id} className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4 flex gap-4 items-start" data-testid={`auction-row-${a.id}`}>
                          {a.images?.[0] && (
                            <img src={a.images[0]} alt="" className="w-24 h-16 object-cover rounded-md flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-white text-sm truncate">{a.brand} {a.model} ({a.year})</span>
                                {auctionStatusBadge(status)}
                              </div>
                              <span className="text-[10px] font-mono text-white/40">#{a.id?.slice(0,8)}</span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-[11px] text-white/60">
                              <div>Induló: <span className="text-white">{(a.dealer_start_price || 0).toLocaleString("hu-HU")} €</span></div>
                              <div>Aktuális: <span className="text-[#D4AF37]">{(a.dealer_highest_bid || 0).toLocaleString("hu-HU")} €</span></div>
                              <div>Licitek: <span className="text-white">{a.bid_count || 0}</span></div>
                              <div>Rezerv: <span className="text-white">{(a.reserve_price || 0).toLocaleString("hu-HU")} €</span></div>
                              <div className="col-span-2 sm:col-span-4">Vég: <span className="text-white/80">{endStr}</span></div>
                              {a.winner_dealer_id && (
                                <div className="col-span-2 sm:col-span-4">Nyertes: <span className="text-[#D4AF37] font-medium">dealer #{a.winner_dealer_id.slice(0,8)}</span> — {(a.winner_bid_amount || 0).toLocaleString("hu-HU")} €</div>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-3">
                              {(status === "draft" || status === "scheduled") && (
                                <button onClick={() => startAuction(a.id)} className="px-2 py-1 rounded bg-green-600/20 text-green-400 hover:bg-green-600/30 text-[10px]" data-testid={`auction-start-${a.id}`}>Start</button>
                              )}
                              {status === "live" && (
                                <>
                                  <button onClick={() => auctionAction(a.id, "pause")} className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 text-[10px]" data-testid={`auction-pause-${a.id}`}>Szünet</button>
                                  <button onClick={() => extendAuction(a.id)} className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 text-[10px]" data-testid={`auction-extend-${a.id}`}>Extend</button>
                                  <button onClick={() => endAuction(a.id)} className="px-2 py-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 text-[10px]" data-testid={`auction-end-${a.id}`}>End</button>
                                </>
                              )}
                              {status === "paused" && (
                                <button onClick={() => auctionAction(a.id, "resume")} className="px-2 py-1 rounded bg-green-600/20 text-green-400 hover:bg-green-600/30 text-[10px]" data-testid={`auction-resume-${a.id}`}>Folytatás</button>
                              )}
                              <button onClick={() => openAuctionDetail(a.id)} className="px-2 py-1 rounded bg-white/5 text-white/70 hover:bg-white/10 text-[10px]" data-testid={`auction-detail-${a.id}`}>Részletek</button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {auctionDetail && (
                  <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setAuctionDetail(null)}>
                    <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="auction-detail-modal">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-white font-semibold">Aukció részletek — {auctionDetail.car.brand} {auctionDetail.car.model}</h3>
                        <button onClick={() => setAuctionDetail(null)} className="text-white/60 hover:text-white text-2xl">×</button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3 text-xs text-white/70 mb-4">
                        <div>Státusz: {auctionStatusBadge(auctionDetail.car.auction_status)}</div>
                        <div>Rezerv: {(auctionDetail.car.reserve_price || 0).toLocaleString("hu-HU")} €</div>
                        <div>Induló ár: {(auctionDetail.car.dealer_start_price || 0).toLocaleString("hu-HU")} €</div>
                        <div>Aktuális licit: <span className="text-[#D4AF37]">{(auctionDetail.car.dealer_highest_bid || 0).toLocaleString("hu-HU")} €</span></div>
                        {auctionDetail.winner_dealer && (
                          <div className="sm:col-span-2 pt-2 border-t border-white/5 mt-2">
                            Nyertes: <span className="text-[#D4AF37]">{auctionDetail.winner_dealer.company_name}</span> ({auctionDetail.winner_dealer.email})
                          </div>
                        )}
                        {auctionDetail.order && (
                          <div className="sm:col-span-2">Rendelés: <span className="font-mono text-white">{auctionDetail.order.order_number}</span> — státusz: {auctionDetail.order.status}</div>
                        )}
                      </div>

                      {/* Phase 5 UI: Payment section for auction orders */}
                      {auctionDetail.order && (
                        <div className="mb-4 p-3 bg-[#0F0F0F] rounded-lg border border-[#D4AF37]/20" data-testid="admin-payment-section">
                          <div className="text-xs font-semibold text-[#D4AF37] mb-2">Fizetés (banki átutalás)</div>
                          <div className="grid sm:grid-cols-2 gap-2 text-[11px] text-white/70">
                            <div>Fizetési státusz: <b className="text-white">{auctionDetail.order.payment_status || "pending"}</b></div>
                            <div>Fizetendő teljes: <b className="text-[#D4AF37]">{(auctionDetail.order.pricing_snapshot?.total || 0).toLocaleString("hu-HU")} €</b></div>
                            <div>Határidő: <b className="text-white">{auctionDetail.order.payment_deadline_at ? new Date(auctionDetail.order.payment_deadline_at).toLocaleString("hu-HU") : "-"}</b></div>
                            <div>Bizonylat: {auctionDetail.order.payment_proof_url ? (
                              <a href={auctionDetail.order.payment_proof_url} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline" data-testid="admin-view-proof">Megtekintés</a>
                            ) : <span className="text-white/40">nincs</span>}</div>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {auctionDetail.order.payment_status !== "confirmed" && (
                              <button onClick={() => confirmPayment(auctionDetail.order.id)}
                                      className="px-2 py-1 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30 text-[10px]"
                                      data-testid="admin-confirm-payment">FIZETÉS MEGERŐSÍTÉSE</button>
                            )}
                            {auctionDetail.order.payment_status !== "confirmed" && (
                              <button onClick={() => rejectPayment(auctionDetail.order.id)}
                                      className="px-2 py-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 text-[10px]"
                                      data-testid="admin-reject-payment">Fizetés elutasítása</button>
                            )}
                            <button onClick={() => extendPaymentDeadline(auctionDetail.order.id)}
                                    className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 text-[10px]"
                                    data-testid="admin-extend-deadline">Határidő hosszabbítás</button>
                            <button onClick={() => loadPaymentEvents(auctionDetail.order.id)}
                                    className="px-2 py-1 rounded bg-white/5 text-white/70 hover:bg-white/10 text-[10px]"
                                    data-testid="admin-payment-events-btn">Audit log</button>
                          </div>
                          {paymentEvents.length > 0 && (
                            <div className="mt-3 space-y-1 text-[10px] font-mono max-h-40 overflow-y-auto">
                              {paymentEvents.map(e => (
                                <div key={e.id} className="flex justify-between border-b border-white/5 py-1">
                                  <span className="text-white/60">{e.previous_status} → <b className="text-white">{e.new_status}</b></span>
                                  <span className="text-white/40">{e.actor_type}</span>
                                  <span className="text-white/40">{new Date(e.created_at).toLocaleString("hu-HU")}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="hidden">{/*end-payment-section*/}</div>
                      <div className="mb-4">
                        <h4 className="text-white/80 text-sm mb-2">Bid history ({auctionDetail.bids.length})</h4>
                        <div className="space-y-1 max-h-52 overflow-y-auto">
                          {auctionDetail.bids.map(b => (
                            <div key={b.id} className="flex justify-between text-[11px] text-white/60 border-b border-white/5 py-1">
                              <span>{b.company_name}</span>
                              <span className="text-white">{b.amount.toLocaleString("hu-HU")} €</span>
                              <span className="text-white/40">{new Date(b.created_at).toLocaleTimeString("hu-HU")}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-white/80 text-sm mb-2">Audit ({auctionDetail.bids_audit.length})</h4>
                        <div className="space-y-1 max-h-52 overflow-y-auto text-[10px] font-mono">
                          {auctionDetail.bids_audit.map(a => (
                            <div key={a.id} className={`flex justify-between border-b border-white/5 py-1 ${a.accepted ? "text-green-400" : "text-red-400"}`}>
                              <span>{a.dealer_company || "-"}</span>
                              <span>{a.amount?.toLocaleString("hu-HU")} €</span>
                              <span className="text-white/40">{a.reason}</span>
                              <span className="text-white/40">{a.ip || "-"}</span>
                              <span className="text-white/40">{new Date(a.created_at).toLocaleTimeString("hu-HU")}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pending Cars Tab */}
            {activeTab === "pending" && (
              <div className="space-y-4" data-testid="pending-cars-panel">
                {pendingCars.length > 0 ? (
                  pendingCars.map(pc => (
                    <div key={pc.id} className="bg-[#0A0A0A] border border-yellow-500/20 rounded-xl overflow-hidden" data-testid={`pending-car-${pc.id}`}>
                      <div className="flex flex-col sm:flex-row gap-4 p-4">
                        {/* Image */}
                        {pc.images?.[0] && (
                          <img src={`${BACKEND_URL}${pc.images[0].replace("_full.webp", "_thumb.webp")}`}
                            alt="" className="w-full sm:w-32 h-24 rounded-lg object-cover" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-white">{pc.brand} {pc.model} ({pc.year})</span>
                            <span className="px-2 py-0.5 text-[10px] bg-yellow-500/10 text-yellow-400 rounded-full border border-yellow-500/20">Jóváhagyásra vár</span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs text-white/40">
                            <span>Ár: <span className="text-white">{pc.price} €</span></span>
                            <span>Km: <span className="text-white">{pc.mileage}</span></span>
                            <span>Üzemanyag: <span className="text-white">{pc.fuel}</span></span>
                            <span>Képek: <span className="text-white">{pc.images?.length || 0}</span></span>
                          </div>
                          <div className="mt-2 text-xs text-white/30">
                            Feltöltő: {pc.submitter_name} ({pc.submitter_email})
                          </div>
                          {pc.dealer_start_price > 0 && (
                            <div className="mt-2 px-2 py-1 bg-[#D4AF37]/5 border border-[#D4AF37]/10 rounded-md inline-block">
                              <span className="text-xs text-[#D4AF37]">AI javasolt kereskedői induló ár: <strong>{pc.dealer_start_price?.toLocaleString("hu-HU")} €</strong></span>
                            </div>
                          )}
                        </div>
                        <div className="flex sm:flex-col gap-2 shrink-0">
                          <Button size="sm" onClick={() => {
                            const found = pc;
                            if (found) {
                              setCarForm({
                                brand: found.brand || "", model: found.model || "", year: found.year || "",
                                price: found.price || "", mileage: found.mileage || "", fuel: found.fuel || "",
                                engine: found.engine || "", power: found.power || "", transmission: found.transmission || "",
                                drive: found.drive || "", body_type: found.body_type || "", color: found.color || "",
                                vin: found.vin || "", equipment: found.equipment || "", description: found.description || "",
                                featured: found.featured || false, sold: found.sold || false,
                                reserve_price: found.reserve_price || 0,
                                dealer_start_price: found.dealer_start_price || 0,
                                dealer_auction_end_time: found.dealer_auction_end_time || "",
                                pickup_location_ids: found.pickup_location_ids || [],
                                premium_return_available: found.premium_return_available || false,
                                available_immediately: found.available_immediately || false,
                                available_short_term: found.available_short_term || false,
                                vehicle_origin: found.vehicle_origin || {},
                                vehicle_inspection: found.vehicle_inspection || {},
                                vehicle_history: found.vehicle_history || {},
                                inspection_report: found.inspection_report || {},
                                paint_thickness: found.paint_thickness || {},
                                tire_inspection: found.tire_inspection || { primary: { label: "" }, secondary: { label: "" } },
                              });
                              setEditingCar(found);
                              setShowCarModal(true);
                            }
                          }} className="bg-white/5 hover:bg-white/10 text-white border border-white/10 text-xs">
                            Szerkesztés
                          </Button>
                          <Button size="sm" onClick={() => handleApproveCar(pc.id)}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`approve-car-${pc.id}`}>
                            Jóváhagyás
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleRejectCar(pc.id)}
                            className="text-xs" data-testid={`reject-car-${pc.id}`}>
                            Elutasítás
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-16 bg-[#0A0A0A] rounded-xl border border-white/5">
                    <Check className="w-12 h-12 mx-auto text-green-400/20 mb-4" />
                    <p className="text-white/50">Nincs jóváhagyásra váró hirdetés</p>
                  </div>
                )}
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === "orders" && (
              <div className="space-y-4" data-testid="orders-panel">
                <h2 className="text-lg sm:text-xl font-semibold text-white">Foglalások / Rendelések</h2>
                {orders.length > 0 ? (
                  orders.map(order => {
                    const isAuctionOrder = Boolean(order.from_auction_id);
                    const isPaidState = order.status === "deposit_paid" || order.status === "paid";
                    const statusClasses =
                      isPaidState ? "bg-green-500/10 text-green-400 border-green-500/20" :
                      order.status === "completed" ? "bg-blue-500/10 text-blue-400 border-blue-500/20" :
                      order.status === "cancelled" ? "bg-red-500/10 text-red-400 border-red-500/20" :
                      "bg-yellow-500/10 text-yellow-400 border-yellow-500/20";
                    const statusLabel =
                      isPaidState ? (isAuctionOrder ? "Fizetés megerősítve" : "Foglaló befizetve") :
                      order.status === "completed" ? "Lezárva" :
                      order.status === "cancelled" ? "Lemondva" : "Fizetésre vár";
                    const waPhone = (order.phone || "").replace(/[^\d]/g, "");
                    return (
                      <div key={order.id} className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4" data-testid={`order-${order.id}`}>
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span className="font-semibold text-white">{order.car_title}</span>
                              {order.order_number && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[10px] font-mono font-bold text-[#D4AF37]" data-testid={`admin-order-number-${order.id}`}>
                                  #{order.order_number}
                                </span>
                              )}
                              <span className={`px-2 py-0.5 text-[10px] rounded-full border ${statusClasses}`}>
                                {statusLabel}
                              </span>
                              <span className="text-xs text-[#D4AF37] font-semibold">{Math.round(order.amount || 0)} €</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-white/50">
                              <div>Név: <span className="text-white/80">{order.full_name}</span></div>
                              <div>Email: <a href={`mailto:${order.email}`} className="text-white/80 hover:text-[#D4AF37]">{order.email}</a></div>
                              <div>Telefon: <a href={`tel:${order.phone}`} className="text-white/80 hover:text-[#D4AF37]">{order.phone}</a></div>
                              <div>Létrehozva: <span className="text-white/80">{new Date(order.created_at).toLocaleString("hu-HU")}</span></div>
                              {order.paid_at && (
                                <div>Fizetve: <span className="text-white/80">{new Date(order.paid_at).toLocaleString("hu-HU")}</span></div>
                              )}
                              {order.session_id && (
                                <div className="truncate">Session: <span className="text-white/40 font-mono text-[10px]">{order.session_id}</span></div>
                              )}
                            </div>

                            {/* Booking info */}
                            {order.selected_date && (
                              <div className="mt-3 px-3 py-2 bg-[#D4AF37]/5 border border-[#D4AF37]/15 rounded-lg text-xs" data-testid={`order-booking-${order.id}`}>
                                <span className="text-[#D4AF37] font-semibold">📅 Átvételi időpont:</span>{" "}
                                <span className="text-white/90">{order.selected_date} {order.selected_time}</span>
                                {order.pickup_location_name && (
                                  <span className="text-white/60"> – {order.pickup_location_name}</span>
                                )}
                              </div>
                            )}

                            {/* Shipping method + delivery address (especially important for home delivery) */}
                            {order.shipping_method && (
                              <div className="mt-3 px-3 py-2 bg-blue-500/5 border border-blue-400/20 rounded-lg text-xs" data-testid={`order-shipping-${order.id}`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-blue-300 font-semibold">🚚 Átvételi mód:</span>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-500/15 border border-blue-400/30 text-[10px] font-semibold text-blue-200 uppercase tracking-wider">
                                    {order.shipping_method === "home" ? "Házhozszállítás" : order.shipping_method === "papa" ? "Pápa átvétel" : "Ausztria átvétel"}
                                  </span>
                                  {(order.shipping_cost || 0) > 0 && (
                                    <span className="text-white/50 text-[10px]">{order.shipping_cost} €</span>
                                  )}
                                </div>
                                {order.shipping_method === "home" && order.delivery_address && (
                                  <div className="mt-1.5 pl-1 text-white/80 text-[11px]" data-testid={`order-delivery-address-${order.id}`}>
                                    <span className="text-white/50">Szállítási cím:</span> <span className="text-white">{order.delivery_address}</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Status step selector */}
                            {isPaidState && (
                              <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`order-step-control-${order.id}`}>
                                <span className="text-[11px] text-white/50">Lépés:</span>
                                <select
                                  value={order.status_step || 1}
                                  onChange={async (e) => {
                                    try {
                                      await axios.put(`${API}/admin/orders/${order.id}?token=${token}`, { status_step: parseInt(e.target.value, 10) });
                                      toast.success("Lépés frissítve");
                                      fetchData();
                                    } catch { toast.error("Hiba a frissítéskor"); }
                                  }}
                                  className="bg-white/5 border border-white/10 text-white text-xs rounded-md px-2 py-1 focus:border-[#D4AF37]/50 focus:outline-none"
                                  data-testid={`order-step-select-${order.id}`}
                                >
                                  <option value={1}>{isAuctionOrder ? "1. Aukció megnyerve" : "1. Foglaló befizetve"}</option>
                                  <option value={2}>{isAuctionOrder ? "2. Teljes vételár rendezése" : "2. Fennmaradó összeg rendezése"}</option>
                                  <option value={3}>{isAuctionOrder ? "3. Fizetés ellenőrizve" : "3. Kapcsolatfelvétel"}</option>
                                  {order.shipping_method === "home" ? (
                                    <>
                                      <option value={4}>4. Szállítás nyomon követése</option>
                                      <option value={5}>5. Autó átadása</option>
                                    </>
                                  ) : (
                                    <>
                                      <option value={4}>4. Autó beérkezése</option>
                                      <option value={5}>5. Időpont foglalás az átvételre</option>
                                      <option value={6}>6. Autó átvétele</option>
                                    </>
                                  )}
                                </select>
                              </div>
                            )}

                            {/* Transport stage (step 4) — visible when order is at or past step 4 and shipping is austria/papa */}
                            {isPaidState && (order.status_step || 0) >= 4 && ["austria", "papa"].includes(order.shipping_method || "") && (
                              <div className="flex flex-col gap-1 mt-2">
                                <span className="text-[10px] text-white/40 uppercase tracking-wider">Szállítási szakasz</span>
                                <select
                                  defaultValue={order.transport_stage || 0}
                                  onChange={async (e) => {
                                    try {
                                      await axios.put(`${API}/admin/orders/${order.id}?token=${token}`, { transport_stage: parseInt(e.target.value, 10) });
                                      toast.success("Szállítási szakasz frissítve");
                                      fetchData();
                                    } catch { toast.error("Hiba a frissítéskor"); }
                                  }}
                                  className="bg-white/5 border border-white/10 text-white text-xs rounded-md px-2 py-1 focus:border-[#D4AF37]/50 focus:outline-none"
                                  data-testid={`order-transport-stage-${order.id}`}
                                >
                                  <option value={0}>0. Eladónál</option>
                                  <option value={1}>1. AlpineCar telephely</option>
                                  {order.shipping_method === "papa" && <option value={2}>2. Pápai társkereskedés</option>}
                                </select>
                              </div>
                            )}

                            {/* Home-delivery status — only when shipping_method=home */}
                            {isPaidState && (order.shipping_method || "") === "home" && (
                              <div className="flex flex-col gap-1 mt-2">
                                <span className="text-[10px] text-white/40 uppercase tracking-wider">Szállítási státusz (házhoz)</span>
                                <select
                                  defaultValue={order.delivery_status || "Eladónál"}
                                  onChange={async (e) => {
                                    try {
                                      await axios.put(`${API}/admin/orders/${order.id}?token=${token}`, { delivery_status: e.target.value });
                                      toast.success("Szállítási státusz frissítve");
                                      fetchData();
                                    } catch { toast.error("Hiba a frissítéskor"); }
                                  }}
                                  className="bg-white/5 border border-white/10 text-white text-xs rounded-md px-2 py-1 focus:border-[#D4AF37]/50 focus:outline-none"
                                  data-testid={`order-delivery-status-${order.id}`}
                                >
                                  <option value="Eladónál">Eladónál</option>
                                  <option value="AlpineCar telephely">AlpineCar telephely</option>
                                  <option value="Szállítás alatt">Szállítás alatt</option>
                                  <option value="Megérkezett">Megérkezett</option>
                                </select>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 shrink-0">
                            {waPhone && (
                              <a
                                href={`https://wa.me/${waPhone}?text=${encodeURIComponent(`Szia ${order.full_name}! Alpine Car - foglalásoddal kapcsolatban keresünk (${order.car_title}).`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-2 bg-[#25D366] hover:bg-[#20BD5A] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1"
                                data-testid={`order-wa-${order.id}`}
                              >
                                WhatsApp
                              </a>
                            )}
                            {isPaidState && (
                              <Button
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/admin/orders/${order.id}?token=${token}`, { status: "completed" });
                                    toast.success("Rendelés lezárva");
                                    fetchData();
                                  } catch { toast.error("Hiba a frissítéskor"); }
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                data-testid={`order-complete-${order.id}`}
                              >
                                Lezárás
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={async () => {
                                if (!window.confirm("Biztosan törlöd a rendelést?")) return;
                                try {
                                  await axios.delete(`${API}/admin/orders/${order.id}?token=${token}`);
                                  toast.success("Rendelés törölve");
                                  fetchData();
                                } catch { toast.error("Hiba a törléskor"); }
                              }}
                              className="text-xs"
                              data-testid={`order-delete-${order.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-16 bg-[#0A0A0A] rounded-xl border border-white/5">
                    <CreditCard className="w-12 h-12 mx-auto text-white/10 mb-4" />
                    <p className="text-white/50">Még nincsenek foglalások</p>
                  </div>
                )}
              </div>
            )}

            {/* Booking Settings Tab */}
            {activeTab === "booking" && bookingSettings && (
              <div className="space-y-6 max-w-3xl" data-testid="booking-settings-panel">
                <h2 className="text-lg sm:text-xl font-semibold text-white">Időpont beállítások</h2>

                {/* WhatsApp Group URL */}
                <div className="bg-[#0A0A0A] border border-[#25D366]/15 rounded-xl p-4" data-testid="whatsapp-group-settings">
                  <h3 className="text-sm font-semibold text-white/80 mb-1 flex items-center gap-2">
                    <span className="inline-flex w-2 h-2 rounded-full bg-[#25D366]"></span>
                    WhatsApp csoport meghívó link
                  </h3>
                  <p className="text-[11px] text-white/40 mb-3">A vevők/kereskedők ezt a linket kapják meg minden WhatsApp gomb kattintásakor.</p>
                  <div className="flex gap-2 flex-col sm:flex-row">
                    <input
                      type="url"
                      value={whatsappGroupUrl}
                      onChange={(e) => setWhatsappGroupUrl(e.target.value)}
                      placeholder="https://chat.whatsapp.com/XXXXXXX"
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#25D366]/50 focus:outline-none"
                      data-testid="whatsapp-group-url-input"
                    />
                    <Button
                      type="button"
                      onClick={async () => {
                        if (!whatsappGroupUrl.trim()) { toast.error("Adj meg egy URL-t"); return; }
                        setWaUrlSaving(true);
                        try {
                          await axios.put(`${API}/admin/settings/whatsapp-group?token=${token}`, { url: whatsappGroupUrl.trim() });
                          toast.success("WhatsApp link mentve");
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Hiba a mentéskor");
                        } finally {
                          setWaUrlSaving(false);
                        }
                      }}
                      disabled={waUrlSaving}
                      className="bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold whitespace-nowrap"
                      data-testid="whatsapp-group-save"
                    >
                      <Save className="w-4 h-4 mr-2" /> {waUrlSaving ? "Mentés..." : "Mentés"}
                    </Button>
                  </div>
                  {whatsappGroupUrl && (
                    <a
                      href={whatsappGroupUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-[#25D366] hover:underline mt-2 inline-block"
                      data-testid="whatsapp-group-preview-link"
                    >
                      Tesztelés: link megnyitása →
                    </a>
                  )}
                </div>

                {/* Contact settings: phone / whatsapp / email */}
                <div className="bg-[#0A0A0A] border border-[#D4AF37]/15 rounded-xl p-4" data-testid="contact-settings">
                  <h3 className="text-sm font-semibold text-white/80 mb-1 flex items-center gap-2">
                    <span className="inline-flex w-2 h-2 rounded-full bg-[#D4AF37]"></span>
                    Kapcsolati adatok (autó oldal "Érdekel az autó")
                  </h3>
                  <p className="text-[11px] text-white/40 mb-3">Ezeket használja az autó oldalon megnyíló kapcsolat panel: normál hívás, WhatsApp és email.</p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[11px] text-white/50 mb-1">Telefonszám (normál hívás)</label>
                      <input
                        type="tel"
                        value={contactSettings.phone}
                        onChange={(e) => setContactSettings({ ...contactSettings, phone: e.target.value })}
                        placeholder="+43 660 4428 540"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-white/50 mb-1">WhatsApp szám (ország-előhívóval, + nélkül)</label>
                      <input
                        type="text"
                        value={contactSettings.whatsapp}
                        onChange={(e) => setContactSettings({ ...contactSettings, whatsapp: e.target.value })}
                        placeholder="4366044428540"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                        data-testid="contact-whatsapp-input"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-white/50 mb-1">Email cím</label>
                      <input
                        type="email"
                        value={contactSettings.email}
                        onChange={(e) => setContactSettings({ ...contactSettings, email: e.target.value })}
                        placeholder="info@alpinecar.eu"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={async () => {
                      setContactSaving(true);
                      try {
                        const res = await axios.put(`${API}/admin/settings/contact?token=${token}`, {
                          phone: contactSettings.phone.trim(),
                          whatsapp: contactSettings.whatsapp.trim(),
                          email: contactSettings.email.trim(),
                        });
                        setContactSettings(res.data);
                        toast.success("Kapcsolati adatok mentve");
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Hiba a mentéskor");
                      } finally {
                        setContactSaving(false);
                      }
                    }}
                    disabled={contactSaving}
                    className="mt-3 bg-[#D4AF37] hover:bg-[#c4a030] text-black font-semibold"
                    data-testid="contact-settings-save"
                  >
                    <Save className="w-4 h-4 mr-2" /> {contactSaving ? "Mentés..." : "Mentés"}
                  </Button>
                </div>

                {/* Weekdays */}
                <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-white/80 mb-3">Elérhető napok</h3>
                  <div className="flex flex-wrap gap-2">
                    {["Hétfő", "Kedd", "Szerda", "Csütörtök", "Péntek", "Szombat", "Vasárnap"].map((label, wd) => {
                      const on = bookingSettings.available_weekdays?.includes(wd);
                      return (
                        <button
                          key={wd}
                          type="button"
                          onClick={() => toggleWeekday(wd)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${on ? "bg-[#D4AF37] text-black" : "bg-white/5 text-white/50 hover:bg-white/10"}`}
                          data-testid={`booking-weekday-${wd}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time slots */}
                <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-white/80 mb-3">Időpont slotok</h3>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(bookingSettings.time_slots || []).map(t => (
                      <span key={t} className="inline-flex items-center gap-1 bg-white/5 border border-white/10 text-white text-xs px-2.5 py-1 rounded-md" data-testid={`booking-timeslot-${t}`}>
                        {t}
                        <button type="button" onClick={() => removeTimeSlot(t)} className="text-white/40 hover:text-red-400 ml-1">×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="time"
                      onChange={(e) => e.target.value && addTimeSlot(e.target.value)}
                      className="bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:border-[#D4AF37]/50 focus:outline-none"
                      data-testid="booking-timeslot-add"
                    />
                    <span className="text-xs text-white/40 self-center">Válassz időpontot a hozzáadáshoz</span>
                  </div>
                </div>

                {/* Capacity + advance + horizon */}
                <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Max foglalás / slot</label>
                    <input type="number" min={1} value={bookingSettings.max_bookings_per_slot || 1}
                      onChange={(e) => setBookingSettings({ ...bookingSettings, max_bookings_per_slot: parseInt(e.target.value || "1", 10) })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37]/50 focus:outline-none"
                      data-testid="booking-max-per-slot" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Előre min. (nap)</label>
                    <input type="number" min={0} value={bookingSettings.advance_days || 0}
                      onChange={(e) => setBookingSettings({ ...bookingSettings, advance_days: parseInt(e.target.value || "0", 10) })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37]/50 focus:outline-none"
                      data-testid="booking-advance-days" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Időablak (nap)</label>
                    <input type="number" min={1} value={bookingSettings.horizon_days || 30}
                      onChange={(e) => setBookingSettings({ ...bookingSettings, horizon_days: parseInt(e.target.value || "30", 10) })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37]/50 focus:outline-none"
                      data-testid="booking-horizon-days" />
                  </div>
                </div>

                {/* Pickup locations */}
                <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-white/80 mb-3">Átvételi helyszínek</h3>
                  <div className="space-y-2 mb-3">
                    {(bookingSettings.pickup_locations || []).map(loc => (
                      <div key={loc.id} className="flex items-center gap-2 p-2 bg-white/[0.02] border border-white/5 rounded-lg" data-testid={`booking-location-${loc.id}`}>
                        <MapPin className="w-4 h-4 text-[#D4AF37]" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate">{loc.name}</div>
                          <div className="text-[11px] text-white/40 truncate">{loc.address}</div>
                        </div>
                        <button type="button" onClick={() => removePickupLocation(loc.id)} className="text-red-400/70 hover:text-red-400 text-xs px-2" data-testid={`booking-location-remove-${loc.id}`}>Törlés</button>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input type="text" placeholder="Helyszín neve" value={newLocation.name}
                      onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                      data-testid="booking-location-name-input" />
                    <input type="text" placeholder="Cím" value={newLocation.address}
                      onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                      data-testid="booking-location-address-input" />
                  </div>
                  <Button type="button" onClick={addPickupLocation} size="sm" className="mt-2 bg-white/10 hover:bg-white/15 text-white text-xs" data-testid="booking-location-add-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Helyszín hozzáadása
                  </Button>
                </div>

                {/* Save */}
                <Button onClick={saveBookingSettings} className="bg-[#D4AF37] text-black hover:bg-[#B8860B] font-semibold" data-testid="booking-settings-save">
                  <Save className="w-4 h-4 mr-2" /> Beállítások mentése
                </Button>

                {/* Blocked dates */}
                <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-white/80 mb-3">Blokkolt napok</h3>
                  <div className="flex gap-2 mb-3">
                    <input type="date" value={newBlockedDate} onChange={(e) => setNewBlockedDate(e.target.value)}
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37]/50 focus:outline-none"
                      data-testid="booking-blocked-date-input" />
                    <Button type="button" onClick={addBlockedDate} size="sm" className="bg-white/10 hover:bg-white/15 text-white text-xs" data-testid="booking-blocked-date-add">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Blokkolás
                    </Button>
                  </div>
                  {blockedDates.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {blockedDates.map(b => (
                        <span key={b.date} className="inline-flex items-center gap-1 bg-red-500/10 border border-red-500/20 text-red-300 text-xs px-2.5 py-1 rounded-md" data-testid={`booking-blocked-${b.date}`}>
                          {b.date}
                          <button type="button" onClick={() => removeBlockedDate(b.date)} className="text-red-300/70 hover:text-red-200 ml-1">×</button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-white/40">Nincsenek blokkolt napok.</p>
                  )}
                </div>
              </div>
            )}

            {/* Bank settings Tab */}
            {activeTab === "bank" && <AdminBankSettingsTab token={token} />}
          </>
        )}
      </div>
      <Dialog open={showCarModal} onOpenChange={setShowCarModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold">
              {editingCar ? "Autó szerkesztése" : "Új autó hozzáadása"}
            </DialogTitle>
          </DialogHeader>

          {/* Form Tabs */}
          <div className="flex gap-1 bg-white/5 rounded-lg p-1 overflow-x-auto">
            {[
              { id: "basic", label: "Alapadatok", icon: <Car className="w-3.5 h-3.5" /> },
              { id: "details", label: "Adatok", icon: <Globe className="w-3.5 h-3.5" /> },
              { id: "condition", label: "Állapot", icon: <ClipboardCheck className="w-3.5 h-3.5" /> },
              { id: "paint", label: "Fényezés", icon: <AlertTriangle className="w-3.5 h-3.5" /> },
              { id: "tires", label: "Gumik", icon: <Clock className="w-3.5 h-3.5" /> },
              { id: "docs", label: "Dokumentumok", icon: <FileText className="w-3.5 h-3.5" /> },
            ].map((t) => (
              <button key={t.id} type="button" onClick={() => setCarFormTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${carFormTab === t.id ? "bg-[#D4AF37] text-black" : "text-white/50 hover:text-white"}`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
          
          <form onSubmit={handleCarSubmit} className="space-y-4">
            {/* === BASIC TAB === */}
            {carFormTab === "basic" && (
              <>
                {/* Image Upload */}
                <div>
                  <label className="block text-sm text-white/60 mb-2">Képek</label>
                  <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} className="border-2 border-dashed border-white/20 rounded-xl p-4 sm:p-6 text-center hover:border-[#D4AF37]/50 transition-colors">
                    <input type="file" multiple accept="image/*,.heic,.heif" onChange={handleImageUpload} className="hidden" id="image-upload" data-testid="admin-image-upload-input" />
                    <label htmlFor="image-upload" className="cursor-pointer block">
                      {uploading ? <Loader2 className="w-8 h-8 mx-auto text-[#D4AF37] animate-spin" /> : <Upload className="w-8 h-8 mx-auto text-white/40 mb-2" />}
                      <p className="text-sm text-white/60">
                        {uploading
                          ? `Feltöltés... kép ${Math.min(uploadProgress.done + 1, uploadProgress.total)}/${uploadProgress.total}`
                          : "Kattints vagy húzd ide a képeket"}
                      </p>
                      <p className="text-xs text-white/40 mt-1">JPG, PNG, WebP – egyszerre több is</p>
                      {uploading && uploadProgress.total > 0 && (
                        <div className="mt-3 max-w-xs mx-auto" data-testid="admin-upload-progress">
                          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#D4AF37] transition-all"
                              style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </label>
                  </div>
                  {failedFiles.length > 0 && (
                    <div
                      className="mt-3 border border-red-500/40 bg-red-500/5 rounded-lg p-3 space-y-2"
                      data-testid="admin-failed-uploads-panel"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-red-300">
                          Sikertelen feltöltések ({failedFiles.length})
                        </p>
                        <button
                          type="button"
                          data-testid="admin-retry-all-btn"
                          onClick={async () => {
                            // Retry each failed file one-by-one (never restarts successes).
                            for (const entry of [...failedFiles]) {
                              await retryFailedFile(entry);
                            }
                          }}
                          className="text-xs text-[#D4AF37] hover:underline"
                        >
                          Összes újrapróbálása
                        </button>
                      </div>
                      {failedFiles.map(entry => (
                        <div
                          key={entry.id}
                          className="flex items-start gap-2 text-xs"
                          data-testid={`admin-failed-upload-row-${entry.file.name}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-white truncate" title={entry.file.name}>{entry.file.name}</p>
                            <p className="text-red-300/80 break-words">
                              {entry.status ? `HTTP ${entry.status} — ` : ""}{entry.reason}
                            </p>
                          </div>
                          <button
                            type="button"
                            data-testid={`admin-retry-btn-${entry.file.name}`}
                            onClick={() => retryFailedFile(entry)}
                            className="px-2 py-1 rounded bg-[#D4AF37] text-black hover:opacity-90"
                          >
                            Újra
                          </button>
                          <button
                            type="button"
                            data-testid={`admin-dismiss-btn-${entry.file.name}`}
                            onClick={() => dismissFailedFile(entry.id)}
                            className="px-2 py-1 rounded bg-white/10 text-white/70 hover:bg-white/20"
                          >
                            Elvet
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {carForm.images.length > 0 && (
                    <>
                      <p className="text-[11px] text-white/40 mt-3 mb-1 flex items-center gap-1.5"><GripVertical className="w-3 h-3" /> Húzd át a képeket a sorrend módosításához (az első a főkép). Mobilon a ▲▼ gombokkal.</p>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="main-images-grid">
                        {carForm.images.map((img, i) => (
                          <div
                            key={img || i}
                            draggable
                            onDragStart={() => { dragImg.current = i; }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => { e.preventDefault(); moveImage(dragImg.current, i); dragImg.current = null; }}
                            className="relative aspect-square rounded-lg overflow-hidden group border border-white/10 cursor-move bg-[#151517]"
                            data-testid={`main-image-${i}`}
                          >
                            <img
                              src={cldThumb(img, 300)}
                              alt=""
                              className="w-full h-full object-cover pointer-events-none"
                              loading="lazy"
                              onError={(e) => { const el = e.currentTarget; const t = Number(el.dataset.t || 0); if (t >= 1) { el.style.visibility = "hidden"; return; } el.dataset.t = t + 1; el.src = absUrl(img); }}
                            />
                            <div className="absolute top-1 left-1 flex flex-col gap-0.5">
                              <button type="button" disabled={i === 0} onClick={() => moveImage(i, i - 1)} className="w-5 h-5 bg-black/70 rounded flex items-center justify-center disabled:opacity-30 hover:bg-[#D4AF37] hover:text-black" data-testid={`main-image-up-${i}`}><ChevronUp className="w-3 h-3" /></button>
                              <button type="button" disabled={i === carForm.images.length - 1} onClick={() => moveImage(i, i + 1)} className="w-5 h-5 bg-black/70 rounded flex items-center justify-center disabled:opacity-30 hover:bg-[#D4AF37] hover:text-black" data-testid={`main-image-down-${i}`}><ChevronDown className="w-3 h-3" /></button>
                            </div>
                            <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 w-5 h-5 bg-black/70 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500" data-testid={`main-image-remove-${i}`}>
                              <X className="w-3 h-3 text-white" />
                            </button>
                            {i === 0
                              ? <span className="absolute bottom-1 left-1 text-[8px] bg-[#D4AF37] text-black px-1.5 py-0.5 rounded font-bold" data-testid="main-image-primary-badge">FŐKÉP</span>
                              : <span className="absolute bottom-1 left-1 text-[8px] bg-black/70 text-white w-4 h-4 rounded-full flex items-center justify-center">{i + 1}</span>}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Márka *</label>
                    <input type="text" value={carForm.brand} onChange={(e) => setCarForm({...carForm, brand: e.target.value})} required className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. Volkswagen" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Típus *</label>
                    <input type="text" value={carForm.model} onChange={(e) => setCarForm({...carForm, model: e.target.value})} required className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. Passat" />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Évjárat *</label>
                    <input type="text" value={carForm.year} onChange={(e) => setCarForm({...carForm, year: e.target.value})} required className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 2020" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Kilométer *</label>
                    <input type="text" value={carForm.mileage} onChange={(e) => setCarForm({...carForm, mileage: e.target.value})} required className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 85.000 km" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Ár (Ft) *</label>
                    <input type="text" value={carForm.price} onChange={(e) => setCarForm({...carForm, price: e.target.value})} required className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 4 990 000 Ft" />
                  </div>
                </div>
                {/* Origin Country */}
                <div>
                  <label className="block text-xs text-white/60 mb-2">Származási ország</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: "Ausztria", flag: "\u{1F1E6}\u{1F1F9}" },
                      { value: "Magyarország", flag: "\u{1F1ED}\u{1F1FA}" },
                      { value: "Németország", flag: "\u{1F1E9}\u{1F1EA}" },
                    ].map((c) => (
                      <button key={c.value} type="button"
                        onClick={() => setCarForm({...carForm, origin_country: carForm.origin_country === c.value ? "" : c.value})}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                          carForm.origin_country === c.value
                            ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                            : "border-white/10 bg-white/5 text-white/60 hover:border-white/20"
                        }`}
                        data-testid={`origin-country-${c.value}`}
                      >
                        <span className="text-lg leading-none">{c.flag}</span> {c.value}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Üzemanyag</label>
                    <select value={carForm.fuel} onChange={(e) => setCarForm({...carForm, fuel: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                      <option value="Benzin">Benzin</option><option value="Dízel">Dízel</option><option value="Hibrid">Hibrid</option><option value="Elektromos">Elektromos</option><option value="LPG">LPG</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Motor</label>
                    <input type="text" value={carForm.engine} onChange={(e) => setCarForm({...carForm, engine: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 2.0 TDI" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Teljesítmény</label>
                    <input type="text" value={carForm.power} onChange={(e) => setCarForm({...carForm, power: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 150 LE" />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Váltó</label>
                    <select value={carForm.transmission} onChange={(e) => setCarForm({...carForm, transmission: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                      <option value="Automata">Automata</option><option value="Manuális">Manuális</option><option value="DSG">DSG</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Meghajtás</label>
                    <input type="text" value={carForm.drive} onChange={(e) => setCarForm({...carForm, drive: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. Elsőkerék" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Karosszéria</label>
                    <input type="text" value={carForm.body_type} onChange={(e) => setCarForm({...carForm, body_type: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. Kombi" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-white/60 mb-1">Szín</label>
                    <input type="text" value={carForm.color} onChange={(e) => setCarForm({...carForm, color: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. Fehér" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">VIN / Alvázszám</label>
                    <input type="text" value={carForm.vin} onChange={(e) => setCarForm({...carForm, vin: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. WVWZZZ..." />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-white/60 mb-1">Felszereltség</label>
                  <textarea value={carForm.equipment} onChange={(e) => setCarForm({...carForm, equipment: e.target.value})} rows={2} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white resize-none" placeholder="Bőr ülések, navigáció, tolatókamera..." />
                </div>
                <div>
                  <label className="block text-xs text-white/60 mb-1">Leírás</label>
                  <textarea value={carForm.description} onChange={(e) => setCarForm({...carForm, description: e.target.value})} rows={2} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white resize-none" placeholder="Részletes leírás az autóról..." />
                </div>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={carForm.featured} onChange={(e) => setCarForm({...carForm, featured: e.target.checked})} className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#D4AF37]" />
                    <span className="text-sm text-white/70">Kiemelt</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={carForm.sold} onChange={(e) => setCarForm({...carForm, sold: e.target.checked})} className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#D4AF37]" />
                    <span className="text-sm text-white/70">Eladva</span>
                  </label>
                </div>
                {/* Reserve Price */}
                <div className="bg-white/[0.03] border border-white/5 rounded-lg p-3">
                  <label className="block text-xs text-white/60 mb-1">Minimum ár (reserve price) EUR</label>
                  <input type="number" value={carForm.reserve_price || ""} onChange={(e) => setCarForm({...carForm, reserve_price: parseFloat(e.target.value) || 0})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                    placeholder="pl. 12000 (ha az ajánlat ennél alacsonyabb, automatikus új kör indul)"
                    data-testid="reserve-price-input" />
                  <p className="text-[10px] text-white/30 mt-1">Ha a legmagasabb ajánlat nem éri el ezt az összeget, automatikusan új ajánlati kör indul (max 3 kör).</p>
                </div>

                {/* Dealer Start Price */}
                <div className="bg-white/[0.03] border border-[#D4AF37]/10 rounded-lg p-3">
                  <label className="block text-xs text-[#D4AF37] mb-1 font-medium">Kereskedői induló ár (EUR)</label>
                  <input type="number" value={carForm.dealer_start_price || ""} onChange={(e) => setCarForm({...carForm, dealer_start_price: parseFloat(e.target.value) || 0})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                    placeholder="pl. 8000 (ha 0, nem jelenik meg a kereskedői licit)"
                    data-testid="dealer-start-price-input" />
                  <p className="text-[10px] text-white/30 mt-1">Ha kitöltöd, a jóváhagyott kereskedők licitálhatnak erre az autóra. Üresen hagyva nem jelenik meg a kereskedői licit.</p>
                </div>

                {/* Pickup Locations for this car */}
                <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3">
                  <label className="block text-xs text-white/70 mb-2 font-medium">Átvételi helyszínek ehhez az autóhoz</label>
                  <p className="text-[10px] text-white/40 mb-2">Ha egyet sem választasz, az összes helyszín elérhető lesz.</p>
                  <div className="space-y-1.5">
                    {(bookingSettings?.pickup_locations || []).map(loc => {
                      const checked = (carForm.pickup_location_ids || []).includes(loc.id);
                      return (
                        <label key={loc.id} className="flex items-center gap-2 cursor-pointer text-xs text-white/80" data-testid={`car-pickup-loc-${loc.id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const current = carForm.pickup_location_ids || [];
                              setCarForm({
                                ...carForm,
                                pickup_location_ids: e.target.checked
                                  ? [...current, loc.id]
                                  : current.filter(x => x !== loc.id),
                              });
                            }}
                            className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#D4AF37] focus:ring-[#D4AF37]/50"
                          />
                          <span><strong>{loc.name}</strong> – <span className="text-white/50">{loc.address}</span></span>
                        </label>
                      );
                    })}
                    {!bookingSettings?.pickup_locations?.length && (
                      <p className="text-[11px] text-white/40 italic">Nincsenek helyszínek megadva. Add hozzá az &quot;Időpontok&quot; tab-on.</p>
                    )}
                  </div>
                </div>

                {/* Premium return availability */}
                <div className="bg-white/[0.03] border border-[#D4AF37]/10 rounded-lg p-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!carForm.premium_return_available}
                      onChange={(e) => setCarForm({ ...carForm, premium_return_available: e.target.checked })}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#D4AF37] focus:ring-[#D4AF37]/50"
                      data-testid="premium-return-available-checkbox"
                    />
                    <span className="text-xs font-medium text-[#D4AF37]">Prémium visszaküldési jog elérhető (+299 €)</span>
                  </label>
                  <p className="text-[10px] text-white/40 mt-1.5 ml-6">Ha bekapcsolod, a vevő a CarDetail oldalon kiválaszthatja a 24h-os visszaküldési opciót +299 € ellenében.</p>
                </div>

                {/* Availability status (mutually exclusive) */}
                <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3 space-y-2.5">
                  <h4 className="text-xs font-semibold text-white/70 uppercase tracking-wide">Átvételi időzítés</h4>
                  <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-emerald-500/5 transition-colors">
                    <input
                      type="checkbox"
                      checked={!!carForm.available_immediately}
                      onChange={(e) => setCarForm({
                        ...carForm,
                        available_immediately: e.target.checked,
                        // mutually exclusive
                        available_short_term: e.target.checked ? false : carForm.available_short_term,
                      })}
                      className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-emerald-400 focus:ring-emerald-400/50"
                      data-testid="available-immediately-checkbox"
                    />
                    <div className="min-w-0">
                      <span className="block text-xs font-medium text-emerald-300">✔ Azonnal átvehető</span>
                      <span className="block text-[10px] text-white/40 mt-0.5">A CarDetail oldalon zöld pulzáló badge jelenik meg.</span>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-amber-500/5 transition-colors">
                    <input
                      type="checkbox"
                      checked={!!carForm.available_short_term}
                      onChange={(e) => setCarForm({
                        ...carForm,
                        available_short_term: e.target.checked,
                        // mutually exclusive
                        available_immediately: e.target.checked ? false : carForm.available_immediately,
                      })}
                      className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-amber-400 focus:ring-amber-400/50"
                      data-testid="available-short-term-checkbox"
                    />
                    <div className="min-w-0">
                      <span className="block text-xs font-medium text-amber-300">🕒 Rövid időn belül átvehető</span>
                      <span className="block text-[10px] text-white/40 mt-0.5">Néhány napon belül átvehető – borostyán badge.</span>
                    </div>
                  </label>
                  <p className="text-[10px] text-white/30 italic">Egyszerre csak egyik opció lehet aktív.</p>
                </div>
              </>
            )}


            {/* === DETAILS TAB (Origin, Inspection, History) === */}
            {carFormTab === "details" && (
              <>
                {/* Vehicle Origin */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#D4AF37]" /> Jármű eredete
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Helyszín</label>
                      <input type="text" value={carForm.vehicle_origin.location || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_origin: {...carForm.vehicle_origin, location: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. AT 89xx" />
                    </div>
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Távolság (km)</label>
                      <input type="text" value={carForm.vehicle_origin.distance_km || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_origin: {...carForm.vehicle_origin, distance_km: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 74" />
                    </div>
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Forrás</label>
                      <select value={carForm.vehicle_origin.source_type || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_origin: {...carForm.vehicle_origin, source_type: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                        <option value="">Válassz...</option>
                        <option value="Kereskedő">Kereskedő</option>
                        <option value="Magánszemély">Magánszemély</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Importált</label>
                      <select value={carForm.vehicle_origin.imported === true ? "true" : carForm.vehicle_origin.imported === false ? "false" : ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_origin: {...carForm.vehicle_origin, imported: e.target.value === "true" ? true : e.target.value === "false" ? false : null}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                        <option value="">Válassz...</option>
                        <option value="true">Igen</option>
                        <option value="false">Nem</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Utolsó forgalomba helyezés országa</label>
                      <input type="text" value={carForm.vehicle_origin.last_registration_country || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_origin: {...carForm.vehicle_origin, last_registration_country: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. Ausztria" />
                    </div>
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Adózás</label>
                      <input type="text" value={carForm.vehicle_origin.taxation_type || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_origin: {...carForm.vehicle_origin, taxation_type: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. Különbözeti adózás" />
                    </div>
                  </div>
                </div>

                {/* Vehicle Inspection */}
                <div className="pt-4 border-t border-white/10">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Járművizsgálat
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Műszaki érvényes</label>
                      <input type="text" value={carForm.vehicle_inspection.technical_inspection_valid_until || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_inspection: {...carForm.vehicle_inspection, technical_inspection_valid_until: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 12/2025" />
                    </div>
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Utolsó szerviz (km)</label>
                      <input type="text" value={carForm.vehicle_inspection.last_service_km || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_inspection: {...carForm.vehicle_inspection, last_service_km: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 189000 / Ismeretlen" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs text-white/60 mb-1">Szervizkönyv</label>
                      <select value={carForm.vehicle_inspection.service_book_status || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_inspection: {...carForm.vehicle_inspection, service_book_status: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                        <option value="">Válassz...</option>
                        <option value="Teljes">Teljes</option>
                        <option value="Hiányos">Hiányos</option>
                        <option value="Nincs">Nincs</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Vehicle History */}
                <div className="pt-4 border-t border-white/10">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <History className="w-4 h-4 text-[#D4AF37]" /> Járműtörténet
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Első forgalomba helyezés</label>
                      <input type="text" value={carForm.vehicle_history.first_registration || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_history: {...carForm.vehicle_history, first_registration: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 12/2015" />
                    </div>
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Futásteljesítmény (km)</label>
                      <input type="text" value={carForm.vehicle_history.mileage || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_history: {...carForm.vehicle_history, mileage: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 189 187" />
                    </div>
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Tulajdonosok száma</label>
                      <input type="text" value={carForm.vehicle_history.previous_owners || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_history: {...carForm.vehicle_history, previous_owners: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 2" />
                    </div>
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Előélet</label>
                      <select value={carForm.vehicle_history.unusual_usage || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_history: {...carForm.vehicle_history, unusual_usage: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                        <option value="">Válassz...</option>
                        <option value="Nincs">Nincs</option>
                        <option value="Taxi">Taxi</option>
                        <option value="Bérautó">Bérautó</option>
                        <option value="Egyéb">Egyéb</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Kulcsok száma</label>
                      <input type="text" value={carForm.vehicle_history.key_count || ""}
                        onChange={(e) => setCarForm({...carForm, vehicle_history: {...carForm.vehicle_history, key_count: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 2" />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* === CONDITION TAB (Inspection + Damages) === */}
            {carFormTab === "condition" && (
              <>
                {/* Inspection Report */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-[#D4AF37]" /> Személyes ellenőrzés
                  </h3>
                  <label className="flex items-center gap-2 cursor-pointer mb-3 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <input type="checkbox" checked={carForm.inspection_report.nincs_hiba || false}
                      onChange={(e) => setCarForm({...carForm, inspection_report: {...carForm.inspection_report, nincs_hiba: e.target.checked}})}
                      className="w-4 h-4 rounded border-white/20 bg-white/5" />
                    <span className="text-sm text-green-400">Nincs észlelt hiba</span>
                  </label>
                  <div className="space-y-3">
                    {[
                      { key: "motor", label: "Motor állapota" },
                      { key: "valto", label: "Váltó működése" },
                      { key: "futumu", label: "Futómű állapota" },
                      { key: "fekek", label: "Fékek állapota" },
                      { key: "belso", label: "Belső tér állapota" },
                      { key: "menet", label: "Menet közbeni tapasztalatok" },
                      { key: "egyeb", label: "Egyéb megjegyzések" },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="block text-xs text-white/60 mb-1">{label}</label>
                        <textarea value={carForm.inspection_report[key] || ""} rows={2}
                          onChange={(e) => setCarForm({...carForm, inspection_report: {...carForm.inspection_report, [key]: e.target.value}})}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white resize-none" placeholder={`${label}...`} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Damages */}
                <div className="pt-4 border-t border-white/10">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-500" /> Állapot és hibák (fotók)
                  </h3>
                  <div className="space-y-3">
                    {(carForm.damages || []).map((d, i) => (
                      <div key={d.image || `dmg-${i}`} className="flex gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/5">
                        {d.image && (
                          <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={cldThumb(d.image, 300)} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e)=>{const el=e.currentTarget;const t=Number(el.dataset.t||0);if(t>=1){el.style.visibility='hidden';return;}el.dataset.t=t+1;el.src=absUrl(d.image);}} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <input type="text" value={d.description || ""} placeholder="Leírás..."
                            onChange={(e) => {
                              const newDamages = [...carForm.damages];
                              newDamages[i] = { ...newDamages[i], description: e.target.value };
                              setCarForm({...carForm, damages: newDamages});
                            }}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white" />
                        </div>
                        <button type="button" onClick={() => {
                          setCarForm({...carForm, damages: carForm.damages.filter((_, idx) => idx !== i)});
                        }} className="text-red-400 hover:text-red-300 flex-shrink-0 self-center">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <input type="file" accept="image/*,.heic,.heif" multiple className="hidden" id="damage-upload"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = "";
                        if (files.length === 0) return;
                        const { urls, failed } = await uploadMany(files);
                        if (urls.length > 0) {
                          const newEntries = urls.map(u => ({ image: u, description: "" }));
                          setCarForm(prev => ({...prev, damages: [...(prev.damages || []), ...newEntries]}));
                          toast.success(`${urls.length} kép feltöltve${failed.length > 0 ? ` (${failed.length} sikertelen)` : ""}`);
                        } else if (failed.length > 0) toast.error("Feltöltés sikertelen");
                      }} data-testid="admin-damage-upload-input" />
                    <label htmlFor="damage-upload" className="inline-flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white/60 cursor-pointer hover:bg-white/10" data-testid="admin-damage-upload-btn">
                      <Upload className="w-3.5 h-3.5" /> Sérülés fotó hozzáadása (több is)
                    </label>
                  </div>
                </div>

                {/* Motor és egyéb */}
                <div className="pt-4 border-t border-white/10">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-[#D4AF37]" /> Motor és egyéb (fotók/videók)
                  </h3>
                  <div className="space-y-3">
                    {(carForm.motor_images || []).map((item, i) => (
                      <div key={item.url || `motor-${i}`} className="flex gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/5">
                        {item.url && (
                          <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0 relative">
                            {item.type === "video" ? (
                              <>
                                {cldVideoPoster(item.url)
                                  ? <img src={cldVideoPoster(item.url)} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e)=>{e.currentTarget.style.display='none';}} />
                                  : <video src={absUrl(item.url)} className="w-full h-full object-cover" muted preload="metadata" />}
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                  <Play className="w-4 h-4 text-white" />
                                </div>
                              </>
                            ) : (
                              <img src={cldThumb(item.url, 200)} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e)=>{const el=e.currentTarget;const t=Number(el.dataset.t||0);if(t>=1){el.style.visibility='hidden';return;}el.dataset.t=t+1;el.src=absUrl(item.url);}} />
                            )}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <input type="text" value={item.description || ""} placeholder="Leírás (opcionális)..."
                            onChange={(e) => {
                              const updated = [...carForm.motor_images];
                              updated[i] = { ...updated[i], description: e.target.value };
                              setCarForm({...carForm, motor_images: updated});
                            }}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white" />
                          <span className="text-[10px] text-white/30 mt-1 block">{item.type === "video" ? "Videó" : "Kép"}</span>
                        </div>
                        <button type="button" onClick={() => {
                          setCarForm({...carForm, motor_images: carForm.motor_images.filter((_, idx) => idx !== i)});
                        }} className="text-red-400 hover:text-red-300 flex-shrink-0 self-center">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <input type="file" accept="image/*,.heic,.heif" multiple className="hidden" id="motor-img-upload"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = "";
                        if (files.length === 0) return;
                        const { urls, failed } = await uploadMany(files);
                        if (urls.length > 0) {
                          const newEntries = urls.map(u => ({ url: u, type: "image", description: "" }));
                          setCarForm(prev => ({...prev, motor_images: [...(prev.motor_images || []), ...newEntries]}));
                          toast.success(`${urls.length} kép feltöltve${failed.length > 0 ? ` (${failed.length} sikertelen)` : ""}`);
                        } else if (failed.length > 0) toast.error("Feltöltés sikertelen");
                      }} data-testid="admin-motor-img-upload-input" />
                    <label htmlFor="motor-img-upload" className="inline-flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white/60 cursor-pointer hover:bg-white/10" data-testid="admin-motor-img-upload-btn">
                      <Upload className="w-3.5 h-3.5" /> Kép hozzáadása (több is)
                    </label>
                    <input type="file" accept="video/*" multiple className="hidden" id="motor-video-upload"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = "";
                        if (files.length === 0) return;
                        const { urls, failed } = await uploadMany(files, { timeoutMs: 600000 });
                        if (urls.length > 0) {
                          const newEntries = urls.map(u => ({ url: u, type: "video", description: "" }));
                          setCarForm(prev => ({...prev, motor_images: [...(prev.motor_images || []), ...newEntries]}));
                          toast.success(`${urls.length} videó feltöltve${failed.length > 0 ? ` (${failed.length} sikertelen)` : ""}`);
                        } else if (failed.length > 0) toast.error("Videó feltöltés sikertelen");
                      }} data-testid="admin-motor-video-upload-input" />
                    <label htmlFor="motor-video-upload" className="inline-flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white/60 cursor-pointer hover:bg-white/10" data-testid="admin-motor-video-upload-btn">
                      <Play className="w-3.5 h-3.5" /> Videó hozzáadása (több is)
                    </label>
                  </div>
                </div>
              </>
            )}

            {/* === PAINT TAB === */}
            {carFormTab === "paint" && (
              <div>
                <h3 className="text-sm font-semibold mb-3">Fényezésvastagság (µm)</h3>
                <p className="text-xs text-white/40 mb-4">Adja meg a mért értékeket mikronban. Gyári: 90-150 µm.</p>
                <div className="grid grid-cols-2 gap-3">
                  {BODY_PARTS.map(({ key, label }) => (
                    <div key={key}>
                      <label className="block text-xs text-white/60 mb-1">{label}</label>
                      <input type="text" value={carForm.paint_thickness[key] || ""}
                        onChange={(e) => setCarForm({...carForm, paint_thickness: {...carForm.paint_thickness, [key]: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="pl. 120" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* === TIRES TAB === */}
            {carFormTab === "tires" && (
              <div>
                {["primary", "secondary"].map((setKey) => {
                  const setData = carForm.tire_inspection?.[setKey] || {};
                  const isSecondary = setKey === "secondary";
                  const hasAnyData = ["front_left", "front_right", "rear_left", "rear_right"].some(pos => {
                    const t = setData[pos];
                    return t && Object.values(t).some(v => v && v !== "");
                  });
                  return (
                    <div key={setKey} className={isSecondary ? "mt-6 pt-5 border-t border-white/10" : ""}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold">{isSecondary ? "Másodlagos gumik" : "Elsődleges gumik"}</h3>
                        {isSecondary && !hasAnyData && (
                          <span className="text-[10px] text-white/30">Opcionális - pl. nyári gumik ha van téli is</span>
                        )}
                      </div>
                      <div className="mb-3">
                        <label className="block text-xs text-white/60 mb-1">Megnevezés</label>
                        <input type="text" value={setData.label || ""}
                          onChange={(e) => setCarForm(prev => ({...prev, tire_inspection: {...prev.tire_inspection, [setKey]: {...(prev.tire_inspection?.[setKey] || {}), label: e.target.value}}}))}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                          placeholder={isSecondary ? "pl. Nyári gumi" : "pl. Téli gumi"} />
                      </div>
                      <div className="overflow-x-auto -mx-4 sm:mx-0">
                        <div className="min-w-[540px] px-4 sm:px-0">
                          <div className="grid grid-cols-5 gap-2 mb-2">
                            <div className="text-xs text-white/40"></div>
                            {[{k:"front_left",l:"Bal első"},{k:"front_right",l:"Jobb első"},{k:"rear_left",l:"Bal hátsó"},{k:"rear_right",l:"Jobb hátsó"}].map(p => (
                              <div key={p.k} className="text-[11px] text-white/60 text-center font-medium">{p.l}</div>
                            ))}
                          </div>
                          {[
                            {key:"type",label:"Típus",type:"select",options:["","Téli","Nyári","Négyévszakos"]},
                            {key:"dot",label:"DOT",type:"text",ph:"pl. 1234"},
                            {key:"tread_depth",label:"Profil (mm)",type:"text",ph:"pl. 6"},
                            {key:"condition",label:"Állapot",type:"select",options:["","Rendben","Kopott","Csere szükséges"]},
                            {key:"rim_type",label:"Felnitípus",type:"text",ph:"pl. Acél"},
                            {key:"brake_type",label:"Féktípus",type:"text",ph:"pl. Tárcsafék"},
                            {key:"brake_pad",label:"Fékbetét",type:"text",ph:"pl. 5mm"},
                            {key:"brake_disc",label:"Féktárcsa",type:"text",ph:"pl. Rendben"},
                          ].map(field => (
                            <div key={field.key} className="grid grid-cols-5 gap-2 mb-1.5">
                              <div className="text-[11px] text-white/40 flex items-center">{field.label}</div>
                              {["front_left","front_right","rear_left","rear_right"].map(pos => (
                                <div key={pos}>
                                  {field.type === "select" ? (
                                    <select
                                      value={(setData[pos] || {})[field.key] || ""}
                                      onChange={(e) => setCarForm(prev => ({...prev, tire_inspection: {...prev.tire_inspection, [setKey]: {...(prev.tire_inspection?.[setKey] || {}), [pos]: {...(prev.tire_inspection?.[setKey]?.[pos] || {}), [field.key]: e.target.value}}}}))}
                                      className="w-full bg-white/5 border border-white/10 rounded px-1.5 py-1.5 text-[11px] text-white"
                                    >
                                      {field.options.map(o => <option key={o} value={o}>{o || "-"}</option>)}
                                    </select>
                                  ) : (
                                    <input type="text"
                                      value={(setData[pos] || {})[field.key] || ""}
                                      onChange={(e) => setCarForm(prev => ({...prev, tire_inspection: {...prev.tire_inspection, [setKey]: {...(prev.tire_inspection?.[setKey] || {}), [pos]: {...(prev.tire_inspection?.[setKey]?.[pos] || {}), [field.key]: e.target.value}}}}))}
                                      className="w-full bg-white/5 border border-white/10 rounded px-1.5 py-1.5 text-[11px] text-white"
                                      placeholder={field.ph} />
                                  )}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* === DOCS TAB === */}
            {carFormTab === "docs" && (
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#D4AF37]" /> Dokumentumok
                </h3>
                <div className="space-y-3">
                  {(carForm.documents || []).map((doc, i) => (
                    <div key={`doc-${i}-${doc.title}`} className="p-3 bg-white/[0.03] rounded-lg border border-white/5 space-y-2">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                        <input type="text" value={doc.title} placeholder="Dokumentum neve *"
                          onChange={(e) => {
                            const newDocs = [...carForm.documents];
                            newDocs[i] = { ...newDocs[i], title: e.target.value };
                            setCarForm({...carForm, documents: newDocs});
                          }}
                          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white" />
                        <button type="button" onClick={() => {
                          setCarForm({...carForm, documents: carForm.documents.filter((_, idx) => idx !== i)});
                        }} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <input type="text" value={doc.description || ""} placeholder="Leírás (opcionális)"
                        onChange={(e) => {
                          const newDocs = [...carForm.documents];
                          newDocs[i] = { ...newDocs[i], description: e.target.value };
                          setCarForm({...carForm, documents: newDocs});
                        }}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white" />
                      {doc.file_url && (
                        <div className="text-xs text-[#D4AF37] truncate">Feltöltve: {doc.file_url.split('/').pop()}</div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <input type="file" accept="image/*,.pdf,.doc,.docx,.heic,.heif" multiple className="hidden" id="doc-upload"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      e.target.value = "";
                      if (files.length === 0) return;
                      const { urls, failed } = await uploadMany(files);
                      if (urls.length > 0) {
                        // Pair each uploaded URL back with original filename (order is preserved by backend asyncio.gather)
                        const newDocs = urls.map((u, idx) => ({
                          title: (files[idx]?.name || `Dokumentum ${idx + 1}`).replace(/\.[^.]+$/, ''),
                          description: "",
                          file_url: u,
                        }));
                        setCarForm(prev => ({...prev, documents: [...(prev.documents || []), ...newDocs]}));
                        toast.success(`${urls.length} dokumentum feltöltve${failed.length > 0 ? ` (${failed.length} sikertelen)` : ""}`);
                      } else if (failed.length > 0) toast.error("Feltöltés sikertelen");
                    }} data-testid="admin-doc-upload-input" />
                  <label htmlFor="doc-upload" className="inline-flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white/60 cursor-pointer hover:bg-white/10" data-testid="admin-doc-upload-btn">
                    <Upload className="w-3.5 h-3.5" /> Dokumentum feltöltése (több is)
                  </label>
                </div>
                <p className="text-xs text-white/30 mt-3">Pl.: Szervizkönyv, szerviz számlák, műszaki vizsga, CarVertical riport</p>
              </div>
            )}

            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="ghost" onClick={() => setShowCarModal(false)} className="text-white/60">
                Mégse
              </Button>
              <Button type="submit" disabled={saving} data-testid="admin-car-save-btn" className="bg-[#D4AF37] text-black hover:bg-[#B8860B]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editingCar ? "Mentés" : "Hozzáadás"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
